import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { VariableSizeList } from 'react-window';
import {
  Typography, Spin, Input, Tooltip, Alert,
  Button, Skeleton, message, Modal, Table, Result,
} from 'antd';
import { isAxiosError } from 'axios';
import {
  ArrowLeftOutlined, DownOutlined, RightOutlined,
  SearchOutlined, ReloadOutlined, EnvironmentOutlined,
  HistoryOutlined, RobotOutlined, UnorderedListOutlined,
  EditOutlined, PlusOutlined, ArrowRightOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@/contexts/UserContext';
import { useTender } from '@/contexts/TenderContext';
import { RATE_ENGINE_QUERY_KEYS, rateEngineApi } from '../api';
import { useRegionalLibraryItems, useRefreshSiteLibrary, useSiteOppNo } from '../hooks';
import { ChatPanel, ChangeHistoryModal, CensoSessionHistoryModal, parseUtc } from '../components/censo-panel';
import type { AddNewActivityPreviewState, CensoAssistantMode, JustAddedActivityInfo } from '../components/censo-panel';
import type {
  ActivityBreakdownNested,
  ActivitySummary,
  AffectedLeaf,
  BreakdownTreeNode,
  CensoChange,
  LeafSkeleton,
  MutationChangeKind,
  MutationPreviewResponse,
  PageGroupSummary,
  SectionSummary,
  SimulateResponse,
} from '../types';

const { Title, Text } = Typography;

type ViewMode = 'current' | 'diff';

/** Section/Page Group/leaf to auto-expand to while the Activity Assistant's
 *  mutation preview (dry-run, before confirm) has a diff pending. The
 *  confirmed-add flow already has `JustAddedActivityInfo` for auto-expanding
 *  after a write lands (`onActivityAdded`), but nothing did this during the
 *  preview itself — a diff computed against a leaf inside an
 *  already-collapsed Section/Page Group was correct in `simulateResult` but
 *  invisible, since `SectionAccordion`/`PageGroupAccordion` only render their
 *  children (`LeafList`) at all when `open` is true. Derived from the same
 *  `MutationPreviewLeaf.parents` chain (`[pageGroupParent, sectionParent,
 *  rootParent]`) `buildSimulateResultFromMutationPreview` already reads. */
interface MutationPreviewTarget {
  sectionId?: number;
  pageGroupId?: number;
  leafId?: number;
}

const is404 = (err: unknown): boolean => isAxiosError(err) && err.response?.status === 404;
/** React Query `retry` that never retries a 404 — the resource isn't there, retrying won't help. */
const retryUnlessNotFound = (count: number, err: unknown): boolean => !is404(err) && count < 2;
const loadErrorMessage = (err: unknown, what: string): string =>
  is404(err) ? `No ${what} found for this item` : `Failed to load ${what}`;

// ─── Constants ────────────────────────────────────────────────────────────────

const REGIONS = [
  { label: 'North West', value: 'north-west' },
  { label: 'West Midlands', value: 'west-midlands' },
  { label: 'East Midlands', value: 'east-midlands' },
  { label: 'Eastern Counties', value: 'eastern-counties' },
  { label: 'Southern', value: 'southern' },
  { label: 'South West', value: 'south-west' },
  { label: 'Yorkshire', value: 'yorkshire' },
  { label: 'Yorkshire (Morrisons)', value: 'YKSM' },
];

/** Convert a human-readable region label (e.g. "North West") to the API slug (e.g. "north-west"). Returns '' for "All Regions" or unknown values. */
function toRegionSlug(label: string | undefined): string {
  if (!label || label === 'All Regions') return '';
  const found = REGIONS.find(r => r.label.toLowerCase() === label.toLowerCase());
  return found?.value ?? label.toLowerCase().replace(/\s+/g, '-');
}

const RG_COLOURS: Record<string, string> = {
  Labour: '#1890ff',
  Material: '#52c41a',
  Plant: '#fa8c16',
  Subcontract: '#722ed1',
  Other: '#8c8c8c',
};

// Distinct per-`changeKind` treatment for a mutation-preview breakdown row —
// separate from the generic amber "changed" tint used everywhere else in this
// file for a node whose cost simply moved (steady "Modify Existing Activity"
// rate change, or a template row highlighted only because *some* descendant
// changed). `changeKind` marks the *exact* node the AI's operation actually
// touched, so it gets its own colour + a small text tag, rather than blending
// into every other "changed" row around it — this is what lets a user
// pinpoint the one node that was really added/removed among a page of
// merely-recalculated ancestors.
const CHANGE_KIND_STYLES: Record<MutationChangeKind, { label: string; text: string; bg: string; border: string }> = {
  added: { label: 'Added', text: '#389e0d', bg: '#f6ffed', border: '#b7eb8f' },
  modified: { label: 'Modified', text: '#d48806', bg: '#fffbe6', border: '#ffe58f' },
  removed: { label: 'Removed', text: '#cf1322', bg: '#fff1f0', border: '#ffa39e' },
};

const ChangeKindTag: React.FC<{ kind: MutationChangeKind }> = ({ kind }) => {
  const s = CHANGE_KIND_STYLES[kind];
  return (
    <span style={{
      fontSize: 9, fontWeight: 700, lineHeight: '14px', padding: '0 5px',
      borderRadius: 3, color: s.text, background: s.bg, border: `1px solid ${s.border}`,
      textTransform: 'uppercase', letterSpacing: '0.03em', whiteSpace: 'nowrap', flexShrink: 0,
    }}>
      {s.label}
    </span>
  );
};

const VIRTUALISE_THRESHOLD = 100;
const ROW_HEIGHT = 44;

// ─── Section ordering (matches stepper navigation for both workflows) ─────────
// Roads & Sewers: Foul Sewers → Storm Sewers → Roads → Mains Services → Preliminary Items
// Groundworks:    Cut & Fill → Foundations → Drainage → Drives → Service → Paving → Scaffold → Garden → Ret/Screen Walls → Geowall/Tobermore

const SECTION_ORDER_MATCHERS: Array<(n: string) => boolean> = [
  // Roads & Sewers workflow
  n => /foul/i.test(n),
  n => /storm|surface.water/i.test(n),
  n => /road|highway/i.test(n),
  // Groundworks workflow
  n => /cut.{0,5}fill/i.test(n),
  n => /foundation/i.test(n),
  n => /drain/i.test(n),
  n => /drive/i.test(n),
  n => /service/i.test(n) && !/main/i.test(n),
  n => /pav/i.test(n),
  n => /scaffold/i.test(n),
  n => /garden/i.test(n),
  n => /ret|screen.{0,5}wall/i.test(n),
  n => /geo|tober/i.test(n),
  // Always last
  n => /mains?.{0,5}service/i.test(n),
  n => /prelim/i.test(n),
];

function sectionPreferredIndex(name: string): number {
  const idx = SECTION_ORDER_MATCHERS.findIndex(test => test(name));
  return idx === -1 ? SECTION_ORDER_MATCHERS.length : idx;
}

// ─── Simple debounce hook ─────────────────────────────────────────────────────

function useDebounce<T>(value: T, delay = 150): T {
  const [debounced, setDebounced] = useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// Accordion open/closed state backed by localStorage, so a section/page-group
// expanded before navigating away (e.g. to rate-manager) stays expanded on return.
// `forceOpenSignal` — true while this section/page-group is the target of a
// just-confirmed "Add New Activity" (see `justAddedActivity` in
// ActivityTreePage) — force-opens it the same way an explicit user toggle
// would, including persisting the open state, so it stays expanded afterward
// rather than silently re-collapsing once the highlight fades. Mirrors the
// Bill Summary "Add Item" convention (`onNodeAdded` expands and never
// re-collapses the row an item was just added into).
function usePersistedOpen(storageKey: string | null, forceOpenSignal?: boolean): [boolean, () => void] {
  const [open, setOpen] = useState(() => {
    if (!storageKey) return false;
    return localStorage.getItem(storageKey) === '1';
  });
  const toggle = useCallback(() => {
    setOpen(o => {
      const next = !o;
      if (storageKey) localStorage.setItem(storageKey, next ? '1' : '0');
      return next;
    });
  }, [storageKey]);
  // Fires once per time the signal turns on (rising edge), not on every render
  // while it stays on — otherwise a manual collapse flips `open` to false and
  // this effect immediately re-opens it for as long as the preview is showing.
  const handledSignalRef = useRef(false);
  useEffect(() => {
    if (!forceOpenSignal) { handledSignalRef.current = false; return; }
    if (handledSignalRef.current) return;
    handledSignalRef.current = true;
    if (open) return;
    setOpen(true);
    if (storageKey) localStorage.setItem(storageKey, '1');
  }, [forceOpenSignal, open, storageKey]);
  return [open, toggle];
}

// ─── Format helpers ───────────────────────────────────────────────────────────

function fmtCurrency(n: number) {
  return `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ─── Rate change display ──────────────────────────────────────────────────────

/** Inline before → after rate display for diff mode. */
interface RateChangeProps { before: number; after: number; size?: 'sm' | 'md'; }
const RateChange: React.FC<RateChangeProps> = ({ before, after, size = 'md' }) => {
  const delta = after - before;
  if (delta === 0) {
    return <span style={{ fontFamily: 'monospace', fontSize: size === 'sm' ? 11 : 12, color: '#555' }}>{fmtCurrency(after)}</span>;
  }
  const up = delta > 0;
  const pct = before !== 0 ? Math.abs(delta / before) * 100 : 0;
  const fs = size === 'sm' ? 11 : 12;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
      <span style={{ fontFamily: 'monospace', fontSize: fs - 1, color: '#aaa', textDecoration: 'line-through' }}>
        {fmtCurrency(before)}
      </span>
      <span style={{ color: '#999', fontSize: fs - 2 }}>→</span>
      <span style={{ fontFamily: 'monospace', fontSize: fs, fontWeight: 700, color: up ? '#389e0d' : '#cf1322' }}>
        {fmtCurrency(after)}
      </span>
      <span style={{ fontSize: fs - 2, color: up ? '#52c41a' : '#ff4d4f', fontWeight: 500 }}>
        {up ? '+' : ''}{pct.toFixed(1)}%
      </span>
    </span>
  );
};

// ─── Breakdown table ──────────────────────────────────────────────────────────

const BK_COLS = '70px 80px 1fr 48px 34px 34px 34px 46px 50px 118px 74px';
/** Grid-column range (1-indexed, end-exclusive) spanned by the multiplyByA/multiplyByB/divideBy trio. */
const BK_FACTOR_SPAN = '5 / 8';

const BkColumnHeader: React.FC = () => (
  <div style={{ background: '#f5f5f5', borderBottom: '1px solid #e8e8e8' }}>
    {/* Group band — visually clusters the three qty-factor columns so they don't read as noise */}
    <div style={{ display: 'grid', gridTemplateColumns: BK_COLS, gap: 10, padding: '2px 12px 0' }}>
      <span style={{
        gridColumn: BK_FACTOR_SPAN, textAlign: 'center', fontSize: 8.5, fontWeight: 600,
        color: '#b08a2e', letterSpacing: '0.04em', textTransform: 'uppercase',
        borderBottom: '1px solid #e6d5a8', paddingBottom: 1,
      }}>
        Qty factors
      </span>
    </div>
    <div style={{
      display: 'grid', gridTemplateColumns: BK_COLS, gap: 10,
      padding: '4px 12px', fontSize: 10, fontWeight: 700, color: '#b0b0b0', letterSpacing: '0.06em', textTransform: 'uppercase',
      userSelect: 'none',
    }}>
      <span>Resource</span>
      <span>Code</span>
      <span>Description</span>
      <span style={{ textAlign: 'right' }}>Qty</span>
      <Tooltip title="Multiply by A"><span style={{ textAlign: 'center', cursor: 'default' }}>×A</span></Tooltip>
      <Tooltip title="Multiply by B"><span style={{ textAlign: 'center', cursor: 'default' }}>×B</span></Tooltip>
      <Tooltip title="Divide by"><span style={{ textAlign: 'center', cursor: 'default' }}>÷</span></Tooltip>
      <Tooltip title="Waste %"><span style={{ textAlign: 'center', cursor: 'default' }}>W%</span></Tooltip>
      <span style={{ textAlign: 'center' }}>Unit</span>
      <span style={{ textAlign: 'right' }}>Unit Rate</span>
      <span style={{ textAlign: 'right' }}>Line Cost</span>
    </div>
  </div>
);

/** Trims a factor value's trailing zeros for display (API sends fixed-precision decimals, e.g. `1.000000`). */
function fmtFactor(value: number): string {
  return String(Number(value.toFixed(4)));
}

/** Single qty-factor cell — dash only when the field is absent (not applicable to this node); any present value, including 1, is shown. */
const FactorCell: React.FC<{ value?: number; symbol: '×' | '÷' }> = ({ value, symbol }) => {
  if (value === undefined) {
    return <span style={{ textAlign: 'center', color: '#ddd', fontSize: 11 }}>–</span>;
  }
  return (
    <span style={{ textAlign: 'center', fontFamily: 'monospace', fontSize: 10, fontWeight: 600, color: '#b08a2e' }}>
      {symbol}{fmtFactor(value)}
    </span>
  );
};

/** Waste % cell — same "dash only when absent" convention as `FactorCell`, shown verbatim
 * (via `fmtFactor()`) rather than scaled/converted — not part of the multiplyByA/B/divideBy
 * qty-factors band above, so it isn't included in `BK_FACTOR_SPAN`. */
const WastePctCell: React.FC<{ value?: number }> = ({ value }) => {
  if (value === undefined) {
    return <span style={{ textAlign: 'center', color: '#ddd', fontSize: 11 }}>–</span>;
  }
  return (
    <span style={{ textAlign: 'center', fontFamily: 'monospace', fontSize: 10, fontWeight: 600, color: '#666' }}>
      {fmtFactor(value)}%
    </span>
  );
};

// ─── NestedTreeNodeRow — single recursive renderer for v2 nested breakdown ────

// `changeKind` ('added'/'modified'/'removed') is the authoritative "this node
// changed" signal from a mutation preview — `effectiveLineCostBefore`/`before`
// are populated *most* of the time a node changed, but not always: a plain
// "add" (no prior rate to compare against, e.g. a brand-new item placed into
// an existing activity) has been observed to arrive with `changeKind: 'added'`
// and no `before`/`effectiveLineCostBefore` at all. Never rely on the cost
// fields alone to decide "changed" — always OR in `!!n.changeKind`.
function treeHasChanges(nodes: BreakdownTreeNode[]): boolean {
  return nodes.some(n =>
    n.effectiveLineCostBefore !== undefined || n.before !== undefined || !!n.changeKind || treeHasChanges(n.children ?? []),
  );
}

/** Re-bases a mutation-preview breakdown tree's "before" values onto the as-is
 * (steady-state) tree already shown before the preview: only "after" comes from
 * the preview. Nodes are matched by code among siblings; nodes with no as-is
 * counterpart (newly added) keep whatever the preview sent. */
function rebaseTreeBefore(preview: BreakdownTreeNode[], asIs: BreakdownTreeNode[] | undefined): BreakdownTreeNode[] {
  if (!asIs) return preview;
  const used = new Set<number>();
  return preview.map(node => {
    const idx = asIs.findIndex((a, i) => !used.has(i) && a.code === node.code);
    if (idx === -1) return node;
    used.add(idx);
    const base = asIs[idx];
    const costChanged = (node.effectiveLineCost ?? 0) !== (base.effectiveLineCost ?? 0);
    const hadBefore = node.before !== undefined || node.effectiveLineCostBefore !== undefined;
    const children = node.children ? rebaseTreeBefore(node.children, base.children) : node.children;
    if (!costChanged && !hadBefore) return children === node.children ? node : { ...node, children };
    return {
      ...node,
      before: costChanged || node.before !== undefined ? base.r : node.before,
      effectiveLineCostBefore: base.effectiveLineCost,
      children,
    };
  });
}

// Open/closed state of breakdown nodes, kept outside the component: the
// virtualized leaf list unmounts rows that scroll out of view, which would
// otherwise reset every nested accordion to its default (collapsed) on scroll.
// Keyed by activity code + node path; entries persist for the page's lifetime.
const nestedNodeOpenState = new Map<string, boolean>();

const NestedTreeNodeRow: React.FC<{ node: BreakdownTreeNode; depth?: number; shade?: boolean; pathKey: string }> = ({
  node, depth = 0, shade = false, pathKey,
}) => {
  // Auto-expand a template/activity node whose subtree contains a change, so
  // a newly-added item nested a level or more down (e.g. "add TESTITEM01 to
  // New activity") is visible without the user having to know to click in —
  // same reasoning as the Activity Tree's own "just added" auto-expand.
  const [open, setOpenState] = useState(
    () => nestedNodeOpenState.get(pathKey) ?? treeHasChanges(node.children ?? []),
  );
  const setOpen = (update: (o: boolean) => boolean) => {
    const next = update(open);
    nestedNodeOpenState.set(pathKey, next);
    setOpenState(next);
  };
  const isItem = node.type === 'item';
  const changed = node.effectiveLineCostBefore !== undefined || !!node.changeKind;
  // Both are required on `BreakdownTreeNode`, but a mutation-preview node
  // (`MutationPreviewNode`, same declared shape) is only as complete as
  // whatever the dry-run endpoint actually sends — default to 0 rather than
  // let a genuinely missing field crash this row's render.
  const effectiveQty = node.effectiveQty ?? 0;
  const effectiveLineCost = node.effectiveLineCost ?? 0;

  if (isItem) {
    // The exact node an "add"/"modify"/"delete" operation touched gets its
    // own colour (see CHANGE_KIND_STYLES) instead of the generic amber
    // "changed" tint every other affected row uses — this is the "last node"
    // the user actually asked to change, not just something whose cost moved
    // as a result.
    const kindStyle = node.changeKind ? CHANGE_KIND_STYLES[node.changeKind] : undefined;
    return (
      <div style={{
        display: 'grid', gridTemplateColumns: BK_COLS, gap: 10,
        padding: '5px 12px', alignItems: 'center', fontSize: 11,
        background: kindStyle ? kindStyle.bg : (changed ? '#fffbe6' : (shade ? '#fafafa' : '#fff')),
        borderLeft: kindStyle ? `3px solid ${kindStyle.border}` : '3px solid transparent',
        borderBottom: '1px solid #f0f0f0',
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: RG_COLOURS[node.resourceGroup ?? ''] ?? '#8c8c8c', flexShrink: 0 }} />
          <span style={{ fontSize: 10, fontWeight: 600, color: RG_COLOURS[node.resourceGroup ?? ''] ?? '#8c8c8c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {node.resourceGroup}
          </span>
        </span>
        <span style={{ fontFamily: 'monospace', color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={node.code}>
          {node.code}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }} title={node.name}>
          <span style={{ color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {node.name}
          </span>
          {node.changeKind && <ChangeKindTag kind={node.changeKind} />}
        </span>
        <span style={{ fontFamily: 'monospace', color: '#666', textAlign: 'right' }}>{effectiveQty.toFixed(4)}</span>
        <FactorCell value={node.multiplyByA} symbol="×" />
        <FactorCell value={node.multiplyByB} symbol="×" />
        <FactorCell value={node.divideBy} symbol="÷" />
        <WastePctCell value={node.wastePct} />
        <span style={{ color: '#aaa', textAlign: 'center' }}>{node.unit ?? ''}</span>
        <span style={{ textAlign: 'right' }}>
          {node.before !== undefined
            ? <RateChange before={node.before} after={node.r} size="sm" />
            : <span style={{ fontFamily: 'monospace', color: '#555' }}>{fmtCurrency(node.r)}</span>
          }
        </span>
        <span style={{
          fontFamily: 'monospace', textAlign: 'right',
          fontWeight: changed ? 700 : undefined,
          // `?? 0` matters: a `changeKind`-only node (see the comment above
          // `treeHasChanges`) has no `effectiveLineCostBefore` to compare
          // against — compare against 0 (nothing existed before it was added)
          // rather than let `effectiveLineCost > undefined` silently evaluate
          // to `false` for every such node.
          color: changed
            ? (effectiveLineCost > (node.effectiveLineCostBefore ?? 0) ? '#cf1322' : '#389e0d')
            : '#333',
        }}>
          {fmtCurrency(effectiveLineCost)}
        </span>
      </div>
    );
  }

  // Template node — derive before-cost from children when server omits it.
  // `!!c.changeKind` matters here too: a `changeKind`-only child (no cost
  // fields at all — see the comment above `treeHasChanges`) still needs to
  // pull its parent template into "changed"; its own contribution to
  // `derivedBefore` falls back to its current `effectiveLineCost` (0 unless
  // stated otherwise), which is correct — before the add, it contributed
  // nothing.
  const children = node.children ?? [];
  const anyChildChanged = children.some(c => c.effectiveLineCostBefore !== undefined || !!c.changeKind);
  const derivedBefore = anyChildChanged
    ? children.reduce((sum, c) => sum + (c.effectiveLineCostBefore ?? c.effectiveLineCost ?? 0), 0)
    : undefined;
  const templateBefore = node.effectiveLineCostBefore ?? derivedBefore;
  // A `changeKind`-only *template* itself (a brand-new activity, rather than
  // an existing one with a changed child) has no `templateBefore` to derive
  // either — still counts as changed for highlighting, just with nothing to
  // show as a before→after (see the RateChange guard below).
  const templateChanged = templateBefore !== undefined || !!node.changeKind;
  // Same "the exact node an operation touched" distinction as the item
  // branch above — a template is only ever `node.changeKind`-tagged itself
  // when the *activity/sub-activity as a whole* was added/removed (rather
  // than merely containing a changed child further down), so this is rarer
  // in practice but treated identically.
  const kindStyle = node.changeKind ? CHANGE_KIND_STYLES[node.changeKind] : undefined;

  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'grid', gridTemplateColumns: BK_COLS, gap: 10,
          width: '100%', padding: '5px 12px', alignItems: 'center',
          background: kindStyle ? kindStyle.bg : (templateChanged ? '#fff7e6' : '#f9f0ff'),
          borderTop: depth === 0 ? '1px solid #ede0ff' : 'none',
          borderBottom: '1px solid #ede0ff',
          borderLeft: `3px solid ${kindStyle ? kindStyle.border : '#b37feb'}`,
          cursor: 'pointer', border: 'none', textAlign: 'left', fontSize: 11,
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {open
            ? <DownOutlined style={{ fontSize: 9, color: '#722ed1' }} />
            : <RightOutlined style={{ fontSize: 9, color: '#722ed1' }} />}
          <span style={{ fontSize: 10, color: '#722ed1', fontWeight: 700 }}>Activity</span>
        </span>
        <span style={{ fontFamily: 'monospace', color: '#722ed1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {node.code}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <span style={{ color: '#444', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{node.name}</span>
          {node.changeKind && <ChangeKindTag kind={node.changeKind} />}
        </span>
        <span /><span /><span /><span /><span /><span /><span />
        <span style={{ fontFamily: 'monospace', textAlign: 'right', fontWeight: 700 }}>
          {templateBefore !== undefined
            ? <RateChange before={templateBefore} after={effectiveLineCost} size="sm" />
            : <span style={{ color: templateChanged ? '#d46b08' : '#722ed1', fontWeight: templateChanged ? 700 : undefined }}>
                {fmtCurrency(effectiveLineCost)}
              </span>
          }
        </span>
      </button>
      {open && children.map((child, i) => (
        <div key={`${child.code}-${i}`} style={{ borderLeft: '3px solid #d3adf7' }}>
          <NestedTreeNodeRow node={child} depth={depth + 1} shade={i % 2 !== 0} pathKey={`${pathKey}/${child.code}-${i}`} />
        </div>
      ))}
    </div>
  );
};

// ─── NestedBreakdownView — single view for both steady-state and simulate ─────

interface NestedBreakdownViewProps {
  tree: BreakdownTreeNode[];
  activityCode: string;
  activityName: string;
  activityUnit?: string;
  activityRate: number;
  activityRateBefore?: number;
  isSimulated?: boolean;
}

const NestedBreakdownView: React.FC<NestedBreakdownViewProps> = ({
  tree, activityCode, activityName, activityUnit, activityRate, activityRateBefore, isSimulated = false,
}) => {
  const totalCost = tree.reduce((s, n) => s + (n.effectiveLineCost ?? 0), 0);
  const hasChanges = treeHasChanges(tree);
  const headerOrange = isSimulated || activityRateBefore !== undefined;

  return (
    <div style={{ border: '1px solid #ddd', borderRadius: 6, overflow: 'hidden' }}>
      {/* Activity header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
        background: headerOrange ? '#fff7e6' : '#e6f4ff',
        borderBottom: '1px solid ' + (headerOrange ? '#ffd591' : '#bae0ff'),
      }}>
        <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: headerOrange ? '#d46b08' : '#1890ff', flexShrink: 0 }}>
          {activityCode}
        </span>
        <span style={{ fontSize: 13, color: '#222', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {activityName}
        </span>
        {activityUnit && (
          <span style={{ fontSize: 11, color: '#999', flexShrink: 0 }}>per {activityUnit}</span>
        )}
        <span style={{ flexShrink: 0 }}>
          {activityRateBefore !== undefined
            ? <RateChange before={activityRateBefore} after={activityRate} />
            : <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color: headerOrange ? '#d46b08' : '#1890ff' }}>
                {fmtCurrency(activityRate)}
              </span>
          }
        </span>
      </div>

      {/* Column headers */}
      <BkColumnHeader />

      {/* Nested tree rows */}
      {tree.map((node, i) => (
        <NestedTreeNodeRow key={`${node.code}-${i}`} node={node} shade={i % 2 !== 0} pathKey={`${activityCode}/${node.code}-${i}`} />
      ))}

      {/* Total row */}
      <div style={{
        display: 'grid', gridTemplateColumns: BK_COLS, gap: 10, padding: '6px 12px',
        background: hasChanges ? '#fffbe6' : '#f5f5f5',
        borderTop: `2px solid ${hasChanges ? '#ffd591' : '#e8e8e8'}`,
      }}>
        <span style={{ gridColumn: '1 / 11', textAlign: 'right', fontSize: 11, color: '#777', fontWeight: 600 }}>
          Total unit rate
        </span>
        <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: '#222', textAlign: 'right' }}>
          {fmtCurrency(totalCost)}
        </span>
      </div>
    </div>
  );
};

// ─── Leaf row ─────────────────────────────────────────────────────────────────

interface LeafRowProps {
  leaf: LeafSkeleton;
  region: string;
  siteName?: string;
  oppNo?: string;
  affectedLeaf?: AffectedLeaf;
  viewMode?: ViewMode;
  simulateChanges?: CensoChange[];
  simulateBatchId?: string;
  /** Whole preview diff map, forwarded to nested child lists so parent rows
   *  (those with `hasChild`) show preview changes on their descendants too. */
  affectedLeaves?: Record<string, AffectedLeaf[]>;
  showChangedOnly?: boolean;
  /** Forwarded so a parent row that is an ancestor of the just-added activity expands and highlights it. */
  justAddedActivity?: JustAddedActivityInfo | null;
  /** True for exactly one row, right after it was created by a confirmed
   *  "Add New Activity" — see `justAddedActivity` in ActivityTreePage. Plays
   *  a brief amber fade, same convention as every Bill Summary's own
   *  "just-added row" highlight (e.g. FoulSewersBillSummary.tsx). */
  isJustAdded?: boolean;
  /** Only used by the non-virtualized branch of `LeafList`, on the just-added
   *  row, to scroll it into view — plain-prop ref (React 19, no forwardRef
   *  needed per this repo's own convention). */
  rowRef?: React.Ref<HTMLDivElement>;
  /** Only set by the virtualized branch of `LeafList` — called after every
   *  render with this row's actual rendered height, so the surrounding
   *  `VariableSizeList` can lay out subsequent rows correctly once this one
   *  expands inline. See `VirtualRow`. */
  onHeightChange?: (height: number) => void;
  /** Whether this row's inline breakdown is expanded — lifted up to `LeafList`
   *  (keyed by leaf id) rather than local state. `VariableSizeList` (the
   *  virtualized/"Search N items…" branch) unmounts row components that
   *  scroll out of the rendered window and remounts fresh ones when they
   *  scroll back in, so local state here would reset to collapsed on every
   *  scroll — and on any relayout `LeafList` triggers via `resetAfterIndex`
   *  (e.g. after a simulate/preview API call changes `filtered`). Lifting it
   *  means the flag survives the row component being recycled. */
  expanded: boolean;
  onToggleExpand: () => void;
}

const LeafRow: React.FC<LeafRowProps> = ({
  leaf, region, siteName, oppNo,
  affectedLeaf, viewMode = 'current', simulateChanges, simulateBatchId, isJustAdded = false, rowRef, onHeightChange,
  expanded, onToggleExpand, affectedLeaves, showChangedOnly = false, justAddedActivity,
}) => {
  const isAffected = !!affectedLeaf;
  const isDiffMode = viewMode === 'diff';
  // An affected leaf whose breakdown tree already arrived inline — the "Add
  // New Activity" mutation-preview adapter (`buildSimulateResultFromMutationPreview`)
  // has no CensoChange list to simulate a breakdown against, so it embeds the
  // tree directly on `AffectedLeaf.tree` instead. When present, both queries
  // below are skipped entirely and the embedded tree is rendered as-is.
  const embeddedTree = affectedLeaf?.tree;
  const hasSimulateChanges = !!simulateChanges?.length;
  // Rows with `hasChild: true` are parents: expand via the leaves API.
  // Everything else is a true leaf: expand via the breakdown API.
  const hasChildren = leaf.hasChild === true;
  // Preview/simulate: does a changed leaf sit somewhere beneath this parent row?
  const containsChange = hasChildren && viewMode === 'diff' && !!affectedLeaves
    && Object.values(affectedLeaves).some(list => list.some(a =>
      a.ancestors?.some(anc => anc.id === leaf.id)));
  const containsJustAdded = hasChildren && !!justAddedActivity?.ancestors?.some(a => a.id === leaf.id);
  // Auto-expand once so the highlighted descendant is actually on screen.
  const autoExpanded = useRef(false);
  const shouldReveal = containsChange || containsJustAdded;
  useEffect(() => {
    if (shouldReveal && !expanded && !autoExpanded.current) {
      autoExpanded.current = true;
      onToggleExpand();
    }
  }, [shouldReveal, expanded, onToggleExpand]);

  const childLeavesQ = useQuery({
    queryKey: RATE_ENGINE_QUERY_KEYS.activityLeaves(region, leaf.id, siteName, oppNo),
    queryFn: () => rateEngineApi.getActivityLeaves(region, leaf.id, siteName, oppNo),
    enabled: expanded && hasChildren,
    retry: retryUnlessNotFound,
    staleTime: 60_000,
  });
  const childLeaves = Array.isArray(childLeavesQ.data)
    ? (childLeavesQ.data as unknown as LeafSkeleton[])
    : childLeavesQ.data?.items;
  // Preview-only rows whose immediate parent is THIS row (an "Add New Activity"
  // placed inside a nested row) — injected here, not into the Page Group list.
  const nestedPreviewOnly = useMemo<LeafSkeleton[]>(() => {
    if (!hasChildren || viewMode !== 'diff' || !affectedLeaves || !childLeaves) return [];
    const ids = new Set(childLeaves.map(c => c.id));
    const codes = new Set(childLeaves.map(c => c.c));
    return Object.values(affectedLeaves).flat()
      .filter(al => al.ancestors?.[0]?.id === leaf.id && !ids.has(al.id) && !codes.has(al.c))
      .map(al => ({ id: al.id, c: al.c, n: al.n, u: al.u, q: al.q, r: al.r, so: -1, b: al.b, d: al.d }));
  }, [hasChildren, viewMode, affectedLeaves, childLeaves, leaf.id]);
  const nestedJustAddedId = containsJustAdded
    ? childLeaves?.find(c => c.c === justAddedActivity!.code)?.id
    : undefined;

  // Steady-state GET breakdown (current mode, or diff mode with nothing to simulate against)
  const breakdownQ = useQuery<ActivityBreakdownNested>({
    queryKey: RATE_ENGINE_QUERY_KEYS.activityBreakdownNested(region, leaf.c, siteName, oppNo),
    queryFn: () => rateEngineApi.getActivityBreakdownNested(region, leaf.c, siteName, oppNo),
    enabled: expanded && !hasChildren,
    retry: retryUnlessNotFound,
    staleTime: 300_000,
  });

  // Per-leaf simulated breakdown — POST /breakdown?nested=true body={changes}
  const simulatedBreakdownQ = useQuery<ActivityBreakdownNested>({
    queryKey: RATE_ENGINE_QUERY_KEYS.activityBreakdownSimulated(region, leaf.c, simulateBatchId ?? '', siteName, oppNo),
    queryFn: () => rateEngineApi.simulateActivityBreakdown(region, leaf.c, { changes: simulateChanges! }, siteName, oppNo),
    retry: retryUnlessNotFound,
    enabled: expanded && !hasChildren && !embeddedTree && isDiffMode && !!simulateBatchId && hasSimulateChanges,
    staleTime: 300_000,
    // Every simulate/preview call mints a new simulateBatchId, which changes this
    // query's key. Without placeholderData, an already-open row's breakdown would
    // blank out to a spinner on every unrelated API trigger, reading as if the
    // accordion had collapsed. Keep the last breakdown on screen across that churn.
    placeholderData: (previousData) => previousData,
  });

  const handleClick = onToggleExpand;

  // Report this row's real rendered height back to the virtualized list
  // (no-op for the non-virtualized branch, which never passes this) whenever
  // it changes — toggling `expanded`, or the breakdown query resolving.
  const measureRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!onHeightChange) return;
    const el = measureRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      const h = entries[0]?.contentRect.height;
      if (h) onHeightChange(h);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [onHeightChange]);

  // "Before" is always the rate this row already shows (leaf.r) — the preview
  // only supplies the "after". Synthetic preview-only rows (so === -1) have no
  // as-is rate, so they fall back to the preview's own before.
  const beforeRate = leaf.so === -1 ? affectedLeaf?.b : leaf.r;
  const hasDiff = viewMode === 'diff' && !!affectedLeaf && beforeRate !== undefined && beforeRate !== affectedLeaf.r;

  return (
    <div ref={measureRef}>
    <div ref={rowRef} style={{ borderBottom: '1px solid #f0f0f0' }} className={isJustAdded ? 'activity-leaf-added' : undefined}>
      {isJustAdded && (
        <style>{`
          @keyframes activity-leaf-added-fade { 0% { background-color: #fef3c7; } 100% { background-color: transparent; } }
          .activity-leaf-added > button { animation: activity-leaf-added-fade 1.8s ease-out; }
        `}</style>
      )}
      <button
        onClick={handleClick}
        style={{
          background: (isAffected || containsChange) && viewMode === 'diff' ? '#fffbe6' : (expanded ? '#f5faff' : 'none'),
          border: 'none', cursor: 'pointer',
          padding: '7px 12px', display: 'grid',
          gridTemplateColumns: '14px 100px 1fr 56px 160px',
          alignItems: 'center', gap: 14,
          width: '100%', textAlign: 'left',
        }}
      >
        <span>
          {expanded
            ? <DownOutlined style={{ fontSize: 10, color: '#1890ff' }} />
            : <RightOutlined style={{ fontSize: 10, color: '#bbb' }} />}
        </span>
        <span style={{ fontSize: 11, fontFamily: 'monospace', color: '#1890ff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {leaf.c}
        </span>
        <span style={{ fontSize: 12, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {leaf.n}
        </span>
        <span style={{ fontSize: 11, color: '#aaa', whiteSpace: 'nowrap', textAlign: 'center' }}>
          {leaf.u ?? '–'}
        </span>
        <span style={{ textAlign: 'right', overflow: 'hidden' }}>
          {hasDiff
            ? <RateChange before={beforeRate!} after={affectedLeaf!.r} />
            : <span style={{ fontSize: 12, color: '#555', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                {fmtCurrency(leaf.r)}
              </span>
          }
        </span>
      </button>

      {expanded && (
        <div style={{ padding: '8px 10px 10px 10px' }}>
          {hasChildren ? (
            childLeaves ? (
              <LeafList
                leaves={nestedPreviewOnly.length > 0 ? [...nestedPreviewOnly, ...childLeaves] : childLeaves}
                justAddedLeafId={nestedJustAddedId}
                justAddedActivity={justAddedActivity}
                region={region}
                siteName={siteName}
                oppNo={oppNo}
                viewMode={viewMode}
                showChangedOnly={showChangedOnly}
                simulateChanges={simulateChanges}
                simulateBatchId={simulateBatchId}
                // Preview diffs are keyed by Page Group, which a nested list has
                // no id for — flatten into the fallback bucket; lookups are by
                // leaf id/code, so only matching rows pick one up.
                affectedLeaves={affectedLeaves ? {
                  [NO_PARENT_KEY]: Object.values(affectedLeaves).flat().filter(a =>
                    a.ancestors?.some(anc => anc.id === leaf.id)),
                } : undefined}
              />
            ) : (
              <>
                {childLeavesQ.isLoading && <div style={{ padding: 16, textAlign: 'center' }}><Spin size="small" /></div>}
                {childLeavesQ.error && <Alert type="error" message={loadErrorMessage(childLeavesQ.error, 'child activities')} banner showIcon={false} style={{ fontSize: 11 }} />}
              </>
            )
          ) : embeddedTree ? (
            <NestedBreakdownView
              tree={rebaseTreeBefore(embeddedTree, breakdownQ.data?.tree)}
              activityCode={leaf.c}
              activityName={leaf.n}
              activityUnit={leaf.u}
              activityRate={affectedLeaf!.r}
              activityRateBefore={beforeRate}
              isSimulated
            />
          ) : isDiffMode && hasSimulateChanges ? (
            simulatedBreakdownQ.data ? (
              <NestedBreakdownView
                tree={rebaseTreeBefore(simulatedBreakdownQ.data.tree, breakdownQ.data?.tree)}
                activityCode={simulatedBreakdownQ.data.activityCode}
                activityName={simulatedBreakdownQ.data.activityName}
                activityUnit={simulatedBreakdownQ.data.activityUnit || undefined}
                activityRate={simulatedBreakdownQ.data.activityNettRate}
                activityRateBefore={beforeRate}
                isSimulated
              />
            ) : (
              <>
                {simulatedBreakdownQ.isLoading && <div style={{ padding: 16, textAlign: 'center' }}><Spin size="small" /></div>}
                {simulatedBreakdownQ.error && <Alert type="error" message={loadErrorMessage(simulatedBreakdownQ.error, 'simulated breakdown')} banner showIcon={false} style={{ fontSize: 11 }} />}
              </>
            )
          ) : breakdownQ.data ? (
            <NestedBreakdownView
              tree={breakdownQ.data.tree}
              activityCode={breakdownQ.data.activityCode}
              activityName={breakdownQ.data.activityName}
              activityUnit={breakdownQ.data.activityUnit || undefined}
              activityRate={breakdownQ.data.activityNettRate}
            />
          ) : (
            <>
              {breakdownQ.isLoading && <div style={{ padding: 16, textAlign: 'center' }}><Spin size="small" /></div>}
              {breakdownQ.error && <Alert type="error" message={loadErrorMessage(breakdownQ.error, 'breakdown')} banner showIcon={false} style={{ fontSize: 11 }} />}
            </>
          )}
        </div>
      )}
    </div>
    </div>
  );
};

// ─── Leaf list ────────────────────────────────────────────────────────────────

type VirtualRowData = {
  leaves: LeafSkeleton[];
  region: string;
  siteName?: string;
  oppNo?: string;
  /** Resolves a rendered leaf to its mutation-preview diff, if any — tries
   *  `id` then falls back to `code` (see `LeafList`'s own comment on why). */
  getAffectedLeaf: (leaf: LeafSkeleton) => AffectedLeaf | undefined;
  viewMode: ViewMode;
  simulateChanges?: CensoChange[];
  simulateBatchId?: string;
  affectedLeaves?: Record<string, AffectedLeaf[]>;
  showChangedOnly: boolean;
  justAddedActivity?: JustAddedActivityInfo | null;
  justAddedLeafId?: number;
  /** Reports a row's real rendered height back up to the `VariableSizeList`
   *  so it can re-lay-out the list once a row's inline breakdown expands or
   *  its content finishes loading — see `LeafRow`'s own `onHeightChange`. */
  setRowHeight: (index: number, height: number) => void;
  /** Which leaf ids currently have their inline breakdown expanded — lives in
   *  `LeafList`, not in each row, so it survives `VariableSizeList` unmounting
   *  and remounting row components on scroll/relayout. See `LeafRowProps.expanded`. */
  expandedIds: Set<number>;
  onToggleExpand: (id: number) => void;
};

interface VirtualRowRenderProps {
  index: number;
  style: React.CSSProperties;
  data: VirtualRowData;
}

// Details used to render inline, directly under the row, the same as the
// non-virtualized list below — no side Drawer. Height is variable (a row's
// own breakdown can expand to any size), so the list above uses
// `VariableSizeList` and this component measures its own rendered height via
// `onHeightChange` and reports it back so `VariableSizeList` can reflow.
const VirtualRow: React.FC<VirtualRowRenderProps> = ({ index, style, data }) => {
  const leaf = data.leaves[index];
  const onHeightChange = useCallback(
    (height: number) => data.setRowHeight(index, height),
    [data, index],
  );
  const onToggleExpand = useCallback(
    () => data.onToggleExpand(leaf.id),
    [data, leaf.id],
  );
  return (
    <div style={style}>
      <LeafRow
        leaf={leaf}
        region={data.region}
        siteName={data.siteName}
        oppNo={data.oppNo}
        affectedLeaf={data.getAffectedLeaf(leaf)}
        viewMode={data.viewMode}
        isJustAdded={leaf.id === data.justAddedLeafId}
        simulateChanges={data.simulateChanges}
        simulateBatchId={data.simulateBatchId}
        affectedLeaves={data.affectedLeaves}
        showChangedOnly={data.showChangedOnly}
        justAddedActivity={data.justAddedActivity}
        onHeightChange={onHeightChange}
        expanded={data.expandedIds.has(leaf.id)}
        onToggleExpand={onToggleExpand}
      />
    </div>
  );
};

interface LeafListProps {
  leaves: LeafSkeleton[];
  region: string;
  siteName?: string;
  oppNo?: string;
  pgId?: number;
  affectedLeaves?: Record<string, AffectedLeaf[]>;
  viewMode?: ViewMode;
  showChangedOnly?: boolean;
  simulateChanges?: CensoChange[];
  simulateBatchId?: string;
  /** Real leaf id of the just-added activity, once resolved (see
   *  `PageGroupAccordion`'s code-match effect) — highlighted and scrolled
   *  into view if it's in this list. */
  justAddedLeafId?: number;
  justAddedActivity?: JustAddedActivityInfo | null;
}

const LeafList: React.FC<LeafListProps> = ({
  leaves, region, siteName, oppNo, pgId, affectedLeaves, viewMode = 'current', showChangedOnly = false,
  simulateChanges, simulateBatchId, justAddedLeafId, justAddedActivity,
}) => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 150);
  const [listWidth, setListWidth] = useState(800);
  const listContainerRef = React.useRef<HTMLDivElement>(null);

  // Which leaf ids have their inline breakdown expanded — owned here, not by
  // each `LeafRow`, so it survives the virtualized branch's `VariableSizeList`
  // unmounting/remounting row components (scrolling a row out of the rendered
  // window and back, or any relayout triggered by `resetAfterIndex` below).
  const [expandedIds, setExpandedIds] = useState<Set<number>>(() => new Set());
  const onToggleExpand = useCallback((id: number) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  React.useEffect(() => {
    if (!listContainerRef.current) return;
    const ro = new ResizeObserver(entries => {
      setListWidth(entries[0].contentRect.width || 800);
    });
    ro.observe(listContainerRef.current);
    return () => ro.disconnect();
  }, []);

  // Two indices, not one: a mutation preview's `AffectedLeaf.id` is the id
  // from the tree "Add New Activity" actually mutates, which — for a
  // site-specific library — isn't guaranteed to be the same numeric id this
  // page's own (site-scoped) `ActivitySummary`/leaves list uses for the same
  // conceptual activity (works by coincidence for the Regional Rate Library,
  // which *is* that tree). The `code` usually still lines up, so
  // `getAffectedLeaf` below tries `id` first and falls back to `code` —
  // this is what makes a site-specific preview's diff findable at all instead
  // of silently matching nothing.
  const { affectedLeafById, affectedLeafByCode, firstIdByCode } = useMemo(() => {
    const byId = new Map<number, AffectedLeaf>();
    const byCode = new Map<string, AffectedLeaf>();
    if (affectedLeaves) {
      // Merge in NO_PARENT_KEY's entries (leaves whose preview came back with
      // no resolved Page Group) regardless of `pgId` — see NO_PARENT_KEY's
      // own comment. Harmless for any group that doesn't actually render
      // that leaf; only the group whose real `leaves` list contains it will
      // ever look it up via `getAffectedLeaf`.
      const arr = [
        ...(pgId ? affectedLeaves[String(pgId)] ?? [] : []),
        ...(affectedLeaves[NO_PARENT_KEY] ?? []),
      ];
      // The code fallback is only for an entry whose id matches no row in this
      // list (id mismatch on a site-specific library), and then only the FIRST
      // row with that code — otherwise every same-named row would highlight.
      const idsInList = new Set(leaves.map(l => l.id));
      for (const al of arr) {
        byId.set(al.id, al);
        if (!idsInList.has(al.id)) byCode.set(al.c, al);
      }
    }
    const firstIdByCode = new Map<string, number>();
    for (const l of leaves) if (!firstIdByCode.has(l.c)) firstIdByCode.set(l.c, l.id);
    return { affectedLeafById: byId, affectedLeafByCode: byCode, firstIdByCode };
  }, [affectedLeaves, pgId, leaves]);
  const getAffectedLeaf = useCallback(
    (leaf: LeafSkeleton): AffectedLeaf | undefined =>
      affectedLeafById.get(leaf.id)
      ?? (firstIdByCode.get(leaf.c) === leaf.id ? affectedLeafByCode.get(leaf.c) : undefined),
    [affectedLeafById, affectedLeafByCode, firstIdByCode],
  );
  const hasAnyAffectedLeaves = affectedLeafById.size > 0;

  const baseLeaves = useMemo(() => {
    if (!showChangedOnly || !hasAnyAffectedLeaves) return leaves;
    // Parent rows (`hasChild`) stay visible: their descendants load lazily, so a change under them is unknowable here.
    return leaves.filter(l => !!getAffectedLeaf(l) || l.hasChild === true);
  }, [leaves, showChangedOnly, hasAnyAffectedLeaves, getAffectedLeaf]);

  const filtered = useMemo(() => {
    if (!debouncedSearch) return baseLeaves;
    const q = debouncedSearch.toLowerCase();
    return baseLeaves.filter(l => l.c.toLowerCase().includes(q) || l.n.toLowerCase().includes(q));
  }, [baseLeaves, debouncedSearch]);

  // Per-row height cache for the virtualized branch's `VariableSizeList` —
  // starts at `ROW_HEIGHT` (a collapsed row's real height) for every row and
  // grows once a row expands its breakdown inline (see `VirtualRow`'s
  // `onHeightChange`). Reset whenever the visible row set changes (search,
  // "Show Changed Only", or the underlying leaves themselves) — old cached
  // heights would otherwise apply to the wrong rows once indices shift.
  const rowHeights = React.useRef<Map<number, number>>(new Map());
  const virtualListRef = React.useRef<VariableSizeList>(null);
  const getItemSize = useCallback((index: number) => rowHeights.current.get(index) ?? ROW_HEIGHT, []);
  const setRowHeight = useCallback((index: number, height: number) => {
    if (rowHeights.current.get(index) === height) return;
    rowHeights.current.set(index, height);
    virtualListRef.current?.resetAfterIndex(index);
    setHeightVersion(v => v + 1);
  }, []);
  React.useEffect(() => {
    rowHeights.current.clear();
    virtualListRef.current?.resetAfterIndex(0);
    setHeightVersion(v => v + 1);
  }, [filtered]);
  // The list's own viewport height must follow the rows' REAL heights — sizing
  // it as `filtered.length * ROW_HEIGHT` collapsed it to a sliver when a search
  // left 1–2 rows, so an expanded breakdown inside it was clipped/unscrollable.
  // Capped at 600 so a long list stays scrollable.
  const [heightVersion, setHeightVersion] = useState(0);
  const virtualListHeight = useMemo(() => {
    void heightVersion; // re-measure whenever a row reports a new height
    let total = 1;
    for (let i = 0; i < filtered.length; i++) total += getItemSize(i);
    return Math.min(600, total);
  }, [filtered, heightVersion, getItemSize]);

  const listData = useMemo<VirtualRowData>(() => ({
    leaves: filtered, region, siteName, oppNo,
    getAffectedLeaf, viewMode, simulateChanges, simulateBatchId, affectedLeaves, showChangedOnly, justAddedActivity, justAddedLeafId, setRowHeight,
    expandedIds, onToggleExpand,
  }), [filtered, affectedLeaves, showChangedOnly, justAddedActivity, region, siteName, oppNo, getAffectedLeaf, viewMode, simulateChanges, simulateBatchId, justAddedLeafId, setRowHeight, expandedIds, onToggleExpand]);

  const isLarge = leaves.length > VIRTUALISE_THRESHOLD;

  // Scroll the just-added row into view once it's resolved — virtualized
  // lists via `VariableSizeList`'s own `scrollToItem`, non-virtualized ones via
  // a plain ref + `scrollIntoView` on that one row (see `justAddedRowRef`
  // below). Re-runs on `filtered` too, since search/showChangedOnly can
  // change the row's index (or drop it out of view) after the first resolve.
  const justAddedRowRef = React.useRef<HTMLDivElement | null>(null);
  const justAddedIndex = useMemo(
    () => (justAddedLeafId == null ? -1 : filtered.findIndex(l => l.id === justAddedLeafId)),
    [filtered, justAddedLeafId],
  );
  React.useEffect(() => {
    if (justAddedIndex < 0) return;
    if (isLarge) virtualListRef.current?.scrollToItem(justAddedIndex, 'smart');
    else justAddedRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [justAddedIndex, isLarge]);

  return (
    <div style={{ background: '#fafafa', borderRadius: 4 }}>
      {isLarge && (
        <div style={{ padding: '6px 10px', borderBottom: '1px solid #f0f0f0' }}>
          <Input
            prefix={<SearchOutlined style={{ color: '#bbb' }} />}
            placeholder={`Search ${leaves.length} items…`}
            value={search}
            onChange={e => setSearch(e.target.value)}
            size="small"
            allowClear
            style={{ maxWidth: 360 }}
          />
        </div>
      )}

      {isLarge ? (
        <>
          <div ref={listContainerRef} style={{ width: '100%' }}>
            <VariableSizeList
              ref={virtualListRef}
              height={virtualListHeight}
              itemCount={filtered.length}
              itemSize={getItemSize}
              estimatedItemSize={ROW_HEIGHT}
              width={listWidth}
              itemData={listData}
            >
              {({ index, style, data }: { index: number; style: React.CSSProperties; data: unknown }) => (
                <VirtualRow index={index} style={style} data={data as VirtualRowData} />
              )}
            </VariableSizeList>
          </div>
          {filtered.length === 0 && (
            <div style={{ padding: 16, textAlign: 'center', color: '#999', fontSize: 12 }}>No items match "{search}"</div>
          )}
        </>
      ) : (
        <div>
          {filtered.map(leaf => (
            <LeafRow
              key={leaf.id}
              leaf={leaf}
              region={region}
              siteName={siteName}
              oppNo={oppNo}
              affectedLeaf={getAffectedLeaf(leaf)}
              viewMode={viewMode}
              simulateChanges={simulateChanges}
              simulateBatchId={simulateBatchId}
              affectedLeaves={affectedLeaves}
              showChangedOnly={showChangedOnly}
              justAddedActivity={justAddedActivity}
              isJustAdded={leaf.id === justAddedLeafId}
              rowRef={leaf.id === justAddedLeafId ? justAddedRowRef : undefined}
              expanded={expandedIds.has(leaf.id)}
              onToggleExpand={() => onToggleExpand(leaf.id)}
            />
          ))}
          {filtered.length === 0 && leaves.length > 0 && (
            <div style={{ padding: 12, textAlign: 'center', color: '#999', fontSize: 12 }}>No items found</div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Page group accordion (L2) ────────────────────────────────────────────────

interface PageGroupAccordionProps {
  pageGroup: PageGroupSummary;
  region: string;
  siteName?: string;
  oppNo?: string;
  simulateResult?: SimulateResponse;
  viewMode?: ViewMode;
  showChangedOnly?: boolean;
  simulateChanges?: CensoChange[];
  simulateBatchId?: string;
  /** When true the page-group header is hidden and the content is always visible (single-child skip) */
  forceOpen?: boolean;
  /** Namespace for persisting this accordion's expanded state (per user/tender/region) */
  persistNamespace?: string | null;
  /** Set (by ActivityTreePage) right after a confirmed "Add New Activity" —
   *  when its `pageGroupId` matches this group, force-expand and, once the
   *  new leaf's real id is resolved by matching `code` against a refetched
   *  `leavesQ`, highlight + scroll to it. See `JustAddedActivityInfo`. */
  justAddedActivity?: JustAddedActivityInfo | null;
  /** Same force-expand idea as `justAddedActivity`, but for a mutation
   *  *preview* (dry-run, before confirm) rather than a confirmed write — see
   *  `MutationPreviewTarget`. Its `leafId` is already the real activity id
   *  (the leaf already exists), so it's merged straight into `justAddedLeafId`
   *  for the scroll/highlight — no code-matching needed. */
  previewTarget?: MutationPreviewTarget | null;
}

const PageGroupAccordion: React.FC<PageGroupAccordionProps> = ({
  pageGroup, region, siteName, oppNo, simulateResult, viewMode = 'current', showChangedOnly = false,
  simulateChanges, simulateBatchId, forceOpen = false, persistNamespace, justAddedActivity, previewTarget,
}) => {
  const isJustAddedTarget = justAddedActivity?.pageGroupId === pageGroup.id
    || !!justAddedActivity?.ancestors?.some(a => a.id === pageGroup.id);
  const isPreviewTarget = previewTarget?.pageGroupId === pageGroup.id;
  const [open, setOpen] = usePersistedOpen(
    persistNamespace ? `${persistNamespace}_pg_${pageGroup.id}` : null,
    isJustAddedTarget || isPreviewTarget,
  );
  const isOpen = forceOpen || open;

  const leavesQ = useQuery({
    queryKey: RATE_ENGINE_QUERY_KEYS.activityLeaves(region, pageGroup.id, siteName, oppNo),
    queryFn: () => rateEngineApi.getActivityLeaves(region, pageGroup.id, siteName, oppNo),
    retry: retryUnlessNotFound,
    enabled: isOpen || (viewMode === 'diff' && hasUnroutedAffectedLeaf(simulateResult)),
    staleTime: 60_000,
  });

  // Defensive extraction: handle { items: [...] } shape, plain array, or unknown format.
  // Without this, an unexpected API response shape makes 'items' undefined → silent blank.
  // Memoized (not just re-derived inline) so it's a stable dependency for the
  // useMemo hooks below — the unknown-shape `[]` fallback would otherwise be a
  // fresh array every render.
  const rawLeafData = leavesQ.data;
  const leaves = useMemo<LeafSkeleton[] | undefined>(() => (
    rawLeafData === undefined
      ? undefined
      : Array.isArray(rawLeafData)
        ? (rawLeafData as unknown as LeafSkeleton[])
        : Array.isArray(rawLeafData.items)
          ? rawLeafData.items
          : []
  ), [rawLeafData]);
  const leavesLoading = isOpen && leavesQ.isPending;
  const leavesError = leavesQ.error;

  // The new activity's real numeric id, resolved by matching the mutation
  // preview's `code` against this now-refetched leaves list — see
  // `JustAddedActivityInfo`'s own doc comment for why `code` (not an id) is
  // all that's available client-side. `undefined` until the invalidated
  // `leavesQ` above has actually refetched and the new leaf shows up in it.
  const justAddedLeafId = isJustAddedTarget
    ? leaves?.find(l => l.c === justAddedActivity!.code)?.id
    : (isPreviewTarget ? previewTarget!.leafId : undefined);

  // TEMP diagnostics for the "just added" highlight feature — remove once
  // confirmed working against the real backend.
  useEffect(() => {
    if (!isJustAddedTarget) return;
    // eslint-disable-next-line no-console
    console.debug('[ActivityAssistant][justAdded] page group is the target', {
      pageGroupId: pageGroup.id, isOpen, leavesLoading, leavesCount: leaves?.length,
      leafCodes: leaves?.map(l => l.c), wantedCode: justAddedActivity?.code, resolvedLeafId: justAddedLeafId,
    });
  }, [isJustAddedTarget, isOpen, leavesLoading, leaves, justAddedActivity, justAddedLeafId, pageGroup.id]);

  // Preview-only leaves — an affected leaf (from an Add New Activity mutation
  // preview; see `buildSimulateResultFromMutationPreview`) that doesn't exist
  // in this group's live leaves yet, i.e. a genuinely new activity being
  // added. Injected as a synthetic row so the user can see what they're
  // about to create, same as an existing leaf's diff highlighting below —
  // "Modify Existing Activity" never produces one of these (it only ever
  // changes rates on leaves that already exist).
  const previewOnlyLeaves = useMemo<LeafSkeleton[]>(() => {
    if (viewMode !== 'diff' || !simulateResult) return [];
    const existingIds = new Set((leaves ?? []).map(l => l.id));
    // `code` too, not just `id` — a site-specific library's leaf ids aren't
    // guaranteed to match the mutation-preview response's ids (see
    // `LeafList`'s `getAffectedLeaf` comment); without this, an *existing*
    // site-specific leaf whose id merely doesn't line up gets misread as
    // "genuinely new" and duplicated as a synthetic row alongside the real one.
    const existingCodes = new Set((leaves ?? []).map(l => l.c));
    return (simulateResult.affectedLeaves[String(pageGroup.id)] ?? [])
      .filter(al => !existingIds.has(al.id) && !existingCodes.has(al.c))
      // Immediate parent is a nested row, not this group — it renders inside that row instead.
      .filter(al => !al.ancestors?.[0] || al.ancestors[0].id === pageGroup.id || al.ancestors[0].code === pageGroup.c)
      .map(al => ({ id: al.id, c: al.c, n: al.n, u: al.u, q: al.q, r: al.r, so: -1, b: al.b, d: al.d }));
  }, [viewMode, simulateResult, pageGroup.id, pageGroup.c, leaves]);

  const displayLeaves = useMemo(
    () => (previewOnlyLeaves.length > 0 ? [...previewOnlyLeaves, ...(leaves ?? [])] : leaves),
    [previewOnlyLeaves, leaves],
  );

  const simPg = simulateResult?.summary.sections
    .flatMap(s => s.pageGroups)
    .find(pg => pg.id === pageGroup.id);

  const hasDiff = viewMode === 'diff' && simPg?.b !== undefined && simPg.d !== undefined && simPg.d !== 0;
  // True for a Page Group that contains a change with nothing to show as a
  // rate delta — e.g. a newly-added item with a zero rate. `hasDiff` above
  // stays false (nothing to render as before→after), so this drives a
  // lightweight "contains changes" dot instead.
  const hasChangeWithNoDiff = !hasDiff && viewMode === 'diff'
    && (pageGroupHasResolvedChange(pageGroup.id, simPg?.d, simulateResult ?? null)
      || leavesContainPreviewChange(rawLeafData, simulateResult ?? null));
  const containsPreviewChange = hasDiff || hasChangeWithNoDiff
    || (viewMode === 'diff' && leavesContainPreviewChange(rawLeafData, simulateResult ?? null));

  return (
    <div style={{ borderLeft: forceOpen ? 'none' : '3px solid #e6f0ff', marginLeft: forceOpen ? 0 : 16 }}>
      {/* Header hidden when forceOpen — single page group inside its parent section */}
      {!forceOpen && (
        <button
          onClick={setOpen}
          style={{
            background: containsPreviewChange ? '#fffbe6' : isOpen ? '#f0f7ff' : 'none', border: 'none', cursor: 'pointer',
            padding: '8px 14px', display: 'grid',
            gridTemplateColumns: '14px 1fr auto auto',
            alignItems: 'center', gap: 10,
            width: '100%', textAlign: 'left',
            borderBottom: isOpen ? '1px solid #daeeff' : 'none',
          }}
        >
          {isOpen
            ? <DownOutlined style={{ fontSize: 10, color: '#1890ff' }} />
            : <RightOutlined style={{ fontSize: 10, color: '#aaa' }} />
          }
          <span style={{ fontSize: 13, color: isOpen ? '#1890ff' : '#444', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {pageGroup.n}
          </span>
          <span style={{ fontSize: 11, color: '#bbb', whiteSpace: 'nowrap' }}>{pageGroup.leafCount} activities</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
            {hasChangeWithNoDiff && (
              <span
                title="Contains a change with no overall rate impact (e.g. a newly-added item)"
                style={{ width: 7, height: 7, borderRadius: '50%', background: '#fa8c16', flexShrink: 0 }}
              />
            )}
            {hasDiff
              ? <RateChange before={simPg!.b!} after={simPg!.r} size="sm" />
              : <span style={{ fontSize: 12, fontFamily: 'monospace', color: '#666' }}>{fmtCurrency(pageGroup.r)}</span>
            }
          </span>
        </button>
      )}

      {isOpen && (
        <div style={{ marginBottom: 4 }}>
          {/* Leaf column header */}
          {displayLeaves && displayLeaves.length > 0 && (
            <div style={{
              display: 'grid', gridTemplateColumns: '14px 100px 1fr 56px 160px',
              gap: 14, padding: '3px 12px',
              background: '#f7fbff', borderBottom: '1px solid #e6f0ff',
              fontSize: 9, fontWeight: 700, color: '#bbb', letterSpacing: '0.06em', textTransform: 'uppercase',
              userSelect: 'none',
            }}>
              <span />
              <span>Code</span>
              <span>Description</span>
              <span style={{ textAlign: 'center' }}>Unit</span>
              <span style={{ textAlign: 'right' }}>Rate</span>
            </div>
          )}
          {leavesLoading && <div style={{ padding: '8px 10px' }}><Spin size="small" /></div>}
          {!!leavesError && (
            <Alert
              type="error"
              message={loadErrorMessage(leavesError, 'items')}
              action={<Button size="small" onClick={() => leavesQ.refetch()}>Retry</Button>}
              banner
              style={{ margin: '4px 0', fontSize: 11 }}
            />
          )}
          {displayLeaves && displayLeaves.length === 0 && (
            <div style={{ padding: '8px 10px', fontSize: 12, color: '#999' }}>No items in this group</div>
          )}
          {displayLeaves && displayLeaves.length > 0 && (
            <LeafList
              leaves={displayLeaves}
              region={region}
              siteName={siteName}
              oppNo={oppNo}
              pgId={pageGroup.id}
              affectedLeaves={simulateResult?.affectedLeaves}
              viewMode={viewMode}
              showChangedOnly={showChangedOnly}
              simulateChanges={simulateChanges}
              simulateBatchId={simulateBatchId}
              justAddedLeafId={justAddedLeafId}
              justAddedActivity={justAddedActivity}
            />
          )}
        </div>
      )}
    </div>
  );
};

// ─── Section accordion (L1) ───────────────────────────────────────────────────

interface SectionAccordionProps {
  section: SectionSummary;
  region: string;
  siteName?: string;
  oppNo?: string;
  simulateResult?: SimulateResponse;
  viewMode?: ViewMode;
  showChangedOnly?: boolean;
  simulateChanges?: CensoChange[];
  simulateBatchId?: string;
  /** Namespace for persisting this section's and its page-groups' expanded state (per user/tender/region) */
  persistNamespace?: string | null;
  /** See `PageGroupAccordionProps.justAddedActivity` — forwarded straight
   *  through, plus used here to force-expand the matching section itself. */
  justAddedActivity?: JustAddedActivityInfo | null;
  /** See `PageGroupAccordionProps.previewTarget` — forwarded straight through,
   *  plus used here to force-expand the matching section itself. */
  previewTarget?: MutationPreviewTarget | null;
}

const SectionAccordion: React.FC<SectionAccordionProps> = ({
  section, region, siteName, oppNo, simulateResult, viewMode = 'current', showChangedOnly = false,
  simulateChanges, simulateBatchId, persistNamespace, justAddedActivity, previewTarget,
}) => {
  const isJustAddedSectionTarget = justAddedActivity?.sectionId === section.id
    || !!justAddedActivity?.ancestors?.some(a => a.id === section.id);
  const isPreviewSectionTarget = previewTarget?.sectionId === section.id;
  const [open, setOpen] = usePersistedOpen(
    persistNamespace ? `${persistNamespace}_sec_${section.id}` : null,
    isJustAddedSectionTarget || isPreviewSectionTarget,
  );

  // TEMP diagnostics for the "just added" highlight feature — remove once
  // confirmed working against the real backend.
  useEffect(() => {
    if (!justAddedActivity) return;
    // eslint-disable-next-line no-console
    console.debug('[ActivityAssistant][justAdded] section evaluated', {
      sectionId: section.id, sectionName: section.n, wantedSectionId: justAddedActivity.sectionId,
      isMatch: isJustAddedSectionTarget, open,
    });
  }, [justAddedActivity, section.id, section.n, isJustAddedSectionTarget, open]);

  const simSection = simulateResult?.summary.sections.find(s => s.id === section.id);
  const hasDiff = viewMode === 'diff' && simSection?.b !== undefined && simSection.d !== undefined && simSection.d !== 0;
  // Same "contains a change with no net rate impact" indicator as
  // PageGroupAccordion — true when any of this section's own Page Groups
  // has a confirmed (not just unrouted) change but the section's own diff is 0.
  // Same query key as each Page Group's own leaves query, so the cache is
  // shared. Only fetches while an unrouted preview is active (otherwise it just
  // reads whatever opened Page Groups already cached), so a closed Section can
  // still tell whether the previewed leaf is inside it.
  const cachedLeafPayloads = useQueries({
    queries: section.pageGroups.map(pg => ({
      queryKey: RATE_ENGINE_QUERY_KEYS.activityLeaves(region, pg.id, siteName, oppNo),
      queryFn: () => rateEngineApi.getActivityLeaves(region, pg.id, siteName, oppNo),
      enabled: viewMode === 'diff' && hasUnroutedAffectedLeaf(simulateResult),
      staleTime: 60_000,
    })),
  });
  const hasChangeWithNoDiff = !hasDiff && viewMode === 'diff' && !!simulateResult
    && (section.pageGroups.some(pg => {
      const simPg = simulateResult.summary.sections.flatMap(s => s.pageGroups).find(p => p.id === pg.id);
      return pageGroupHasResolvedChange(pg.id, simPg?.d, simulateResult);
    }) || cachedLeafPayloads.some(q => leavesContainPreviewChange(q.data, simulateResult)));

  return (
    <div style={{ borderTop: '1px solid #e8e8e8', borderBottom: '1px solid #e8e8e8', marginBottom: -1 }}>
      <button
        onClick={setOpen}
        style={{
          background: (hasDiff || hasChangeWithNoDiff) ? '#fffbe6' : open ? '#e8f4ff' : 'rgb(249, 250, 251)',
          border: 'none', cursor: 'pointer',
          padding: '11px 16px',
          display: 'grid',
          gridTemplateColumns: '16px 1fr auto auto',
          alignItems: 'center', gap: 12,
          width: '100%', textAlign: 'left',
          /* inset box-shadow for the left accent — no layout shift */
          boxShadow: open ? 'inset 4px 0 0 #1890ff' : 'none',
        }}
      >
        {open
          ? <DownOutlined style={{ fontSize: 12, color: '#1890ff' }} />
          : <RightOutlined style={{ fontSize: 12, color: '#888' }} />
        }
        <span style={{ fontSize: 14, fontWeight: 500, color: open ? '#1890ff' : '#4a4a4a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {section.n}
        </span>
        <span style={{ fontSize: 11, color: '#bbb', whiteSpace: 'nowrap' }}>
          {section.leafCount.toLocaleString()} activities
        </span>
        <span style={{ minWidth: 120, textAlign: 'right', display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
          {hasChangeWithNoDiff && (
            <span
              title="Contains a change with no overall rate impact (e.g. a newly-added item)"
              style={{ width: 7, height: 7, borderRadius: '50%', background: '#fa8c16', flexShrink: 0 }}
            />
          )}
          {hasDiff
            ? <RateChange before={simSection!.b!} after={simSection!.r} />
            : <span style={{ fontSize: 13, fontFamily: 'monospace', color: '#444', fontWeight: 600 }}>
                {fmtCurrency(section.r)}
              </span>
          }
        </span>
      </button>

      {open && (() => {
        const visibleGroups = section.pageGroups
          .slice()
          .sort((a, b) => a.so - b.so)
          .filter(pg => {
            if (!showChangedOnly || !simulateResult) return true;
            const simPg = simulateResult.summary.sections
              .flatMap(s => s.pageGroups)
              .find(p => p.id === pg.id);
            return pageGroupHasVisibleChange(pg.id, simPg?.d, simulateResult);
          });
        const singleChild = visibleGroups.length === 1;
        return visibleGroups.map(pg => (
          <PageGroupAccordion
            key={pg.id}
            pageGroup={pg}
            region={region}
            siteName={siteName}
            oppNo={oppNo}
            simulateResult={simulateResult}
            viewMode={viewMode}
            showChangedOnly={showChangedOnly}
            simulateChanges={simulateChanges}
            simulateBatchId={simulateBatchId}
            forceOpen={singleChild}
            persistNamespace={persistNamespace}
            justAddedActivity={justAddedActivity}
            previewTarget={previewTarget}
          />
        ));
      })()}
    </div>
  );
};

// ─── Add New Activity: adapt its mutate/preview response into simulate state ──
//
// "Modify Existing Activity" drives every diff-mode visual in this file
// (LibraryHeader's total RateChange, SectionAccordion/PageGroupAccordion's
// ripple badges, LeafRow's per-item diff) off one `SimulateResponse` state
// value. Rather than threading a second, parallel set of props through all of
// those components for "Add New Activity"'s own preview mechanism
// (`MutationPreviewResponse` — see censo-panel.tsx), this adapts the mutation
// preview into that same shape so the existing rendering path is reused
// as-is. `simulateChanges`/`simulateBatchId` are deliberately left empty by
// the caller for this path (there's no CensoChange list to simulate a
// breakdown against) — `LeafRow`/`LeafList` instead render straight from the
// breakdown tree already embedded on `AffectedLeaf.tree` below.

// Fallback bucket for `affectedLeaves` (below) used when the backend's preview
// response comes back with an empty `parents` array for the affected leaf —
// observed in practice for some (not all) previews, even though the leaf
// itself is a real, already-rendered activity. Without a resolved Page Group
// id there's nowhere to route a *ripple* (badge on the group/section, or a
// synthetic row for a not-yet-existing leaf), but the leaf's own diff can
// still be shown once it's rendered — `LeafList`'s `getAffectedLeaf` merges
// this bucket in regardless of which Page Group it's currently building the
// index for, and only the group that actually contains this leaf (by id or
// code) will ever look it up. Don't key this by page group id; that's
// exactly the value we don't have here.
const NO_PARENT_KEY = '__no_parent__';

/** Page Group ids that have at least one affected leaf resolved against them —
 * every `affectedLeaves` key except the `NO_PARENT_KEY` fallback bucket. */
function affectedPageGroupIds(simulateResult: SimulateResponse | null): Set<number> {
  if (!simulateResult) return new Set();
  return new Set(
    Object.keys(simulateResult.affectedLeaves)
      .filter(k => k !== NO_PARENT_KEY)
      .map(Number),
  );
}

/** True when there's an affected leaf whose Page Group couldn't be resolved
 * (filed under `NO_PARENT_KEY`) — when this is true, "Show Changed Only"
 * can't safely hide any section/Page Group, since there's no way to tell
 * which one the change actually belongs to. */
function hasUnroutedChange(simulateResult: SimulateResponse | null): boolean {
  return !!simulateResult && (simulateResult.affectedLeaves[NO_PARENT_KEY]?.length ?? 0) > 0;
}

/** An unrouted preview (leaf filed under NO_PARENT_KEY — its `parents` chain
 * didn't resolve) can't say which Section/Page Group holds it, so closed
 * accordions have to look at their leaves to find out. */
function hasUnroutedAffectedLeaf(simulateResult: SimulateResponse | null | undefined): boolean {
  return (simulateResult?.affectedLeaves[NO_PARENT_KEY] ?? []).length > 0;
}

/** True when any of `rawLeaves` (a leaves-query payload: array, `{ items }`, or
 * unknown) is the target of an affected leaf anywhere in `simulateResult` —
 * matched by id or code, under *any* Page Group key including NO_PARENT_KEY.
 * This is what lets a Page Group/Section highlight even when the preview's
 * `parents` chain didn't resolve to it (see NO_PARENT_KEY), because the leaf
 * itself is still rendered inside the right group. */
function leavesContainPreviewChange(rawLeaves: unknown, simulateResult: SimulateResponse | null): boolean {
  if (!simulateResult) return false;
  const leaves: LeafSkeleton[] = Array.isArray(rawLeaves)
    ? rawLeaves as LeafSkeleton[]
    : Array.isArray((rawLeaves as { items?: unknown } | undefined)?.items)
      ? (rawLeaves as { items: LeafSkeleton[] }).items
      : [];
  if (leaves.length === 0) return false;
  const ids = new Set<number>();
  const codes = new Set<string>();
  for (const list of Object.values(simulateResult.affectedLeaves)) {
    for (const al of list) { ids.add(al.id); codes.add(al.c); }
  }
  return leaves.some(l => ids.has(l.id) || codes.has(l.c));
}

/** Whether a Page Group has a *confirmed* change — a nonzero diff, or a
 * resolved affected leaf actually filed under this Page Group's own id.
 * Deliberately does NOT fall back to `hasUnroutedChange` (unlike
 * `pageGroupHasVisibleChange` below): an unrouted change could belong to any
 * Page Group, so treating it as confirmed here would light up a "contains
 * changes" indicator on every Page Group in the tree, not just the right
 * one. Use this for anything that *claims* a specific group changed. */
function pageGroupHasResolvedChange(
  pgId: number,
  simPgDiff: number | undefined,
  simulateResult: SimulateResponse | null,
): boolean {
  if (!simulateResult) return false;
  if (simPgDiff !== undefined && simPgDiff !== 0) return true;
  return (simulateResult.affectedLeaves[String(pgId)] ?? []).length > 0;
}

/** Whether a Page Group counts as "changed" for `showChangedOnly` purposes —
 * not just `simPg.d !== 0`. A newly-added item with a zero rate (or any
 * change that nets out to zero at the Page Group level) still has to stay
 * visible, so this also counts a Page Group as changed when it has a
 * resolved affected leaf at all, or when there's an unrouted change anywhere
 * (can't rule this group out — safe to over-include for a *filter*, unlike
 * for a specific per-group indicator; see `pageGroupHasResolvedChange`). */
function pageGroupHasVisibleChange(
  pgId: number,
  simPgDiff: number | undefined,
  simulateResult: SimulateResponse | null,
): boolean {
  return pageGroupHasResolvedChange(pgId, simPgDiff, simulateResult) || hasUnroutedChange(simulateResult);
}

function buildSimulateResultFromMutationPreview(
  preview: MutationPreviewResponse,
  baseSummary: ActivitySummary,
): { result: SimulateResponse; target: MutationPreviewTarget } | null {
  if (!preview.primary) return null;

  const summary: ActivitySummary = {
    ...baseSummary,
    totals: { ...baseSummary.totals },
    sections: baseSummary.sections.map(s => ({ ...s, pageGroups: s.pageGroups.map(pg => ({ ...pg })) })),
  };

  const affectedLeaves: Record<string, AffectedLeaf[]> = {};

  // `parents` on a preview leaf is [Page Group, Section, project root], in
  // that order — see MutationPreviewLeaf in types/index.ts. `applyTotals` is
  // false for `secondary` (a "move" leaf under a different bill line) so a
  // single root value doesn't get double-written from two leaves. Returns the
  // *resolved* (this page's own) Page Group/Section ids, if any were found —
  // see the `pg`/`s` lookup below for why these can differ from `pgParent`/
  // `sectionParent`'s own `activityId`.
  const applyLeaf = (
    leaf: NonNullable<MutationPreviewResponse['primary']>, applyTotals: boolean,
  ): { pageGroupId?: number; sectionId?: number } => {
    // Positional destructuring (`[pg, section, root]`) breaks once a leaf sits
    // under a parent row (`hasChild`): the chain gains an intermediate entry
    // and every index shifts. Resolve each level by matching against this
    // page's own tree instead.
    //
    // The root is the last entry of a 3+ chain — unless that entry is by CODE a
    // Section/Page Group of this tree (a chain with no root entry). It is taken
    // out BEFORE the id matching below: the root's numeric id can coincide with
    // a Section/Page Group id, which would otherwise resolve the root as one of
    // those and leave the library total with no change at all.
    const last = leaf.parents[leaf.parents.length - 1];
    const lastIsTreeNode = !!last && summary.sections.some(sec =>
      sec.c === last.code || sec.pageGroups.some(p => p.c === last.code));
    const rootParent = leaf.parents.length >= 3 && !lastIsTreeNode ? last : undefined;
    const levels = rootParent ? leaf.parents.slice(0, -1) : leaf.parents;
    const pgParent = levels.find(par =>
      summary.sections.some(sec => sec.pageGroups.some(p => p.id === par.activityId || p.c === par.code)));
    const sectionParent = levels.find(par =>
      summary.sections.some(sec => sec.id === par.activityId || sec.c === par.code));

    // Matched by `id` (the common case) or, failing that, by `code` — not
    // guaranteed to resolve just because `pgParent`/`sectionParent` is a
    // non-empty object. Observed in practice for a preview against an item
    // added inside an *existing* activity's breakdown (as opposed to placing
    // a brand-new activity into a Page Group the frontend already knows
    // about): the backend derives `parents` by walking up from the leaf's own
    // position in its underlying tree, whose numeric ids don't always match
    // this page's own `ActivitySummary` ids for the same conceptual Page
    // Group/Section — the `code` usually still does. `resolvedPageGroupId`/
    // `resolvedSectionId` are *this page's own* ids once matched (never the
    // raw `pgParent.activityId`), which is what makes them safe to force-open
    // an accordion by by (see `MutationPreviewTarget`).
    let resolvedPageGroupId: number | undefined;
    let resolvedSectionId: number | undefined;
    if (pgParent) {
      for (const s of summary.sections) {
        const pg = s.pageGroups.find(p => p.id === pgParent.activityId || p.c === pgParent.code);
        if (pg) {
          // Before = the as-is value already on screen (baseSummary), not the preview's own.
          const basePg = baseSummary.sections.flatMap(bs => bs.pageGroups).find(bp => bp.id === pg.id);
          pg.b = basePg?.r ?? pgParent.before; pg.d = pgParent.after - pg.b; pg.r = pgParent.after;
          resolvedPageGroupId = pg.id;
          break;
        }
      }
    }
    if (sectionParent) {
      const s = summary.sections.find(sec => sec.id === sectionParent.activityId || sec.c === sectionParent.code);
      if (s) {
        const baseSec = baseSummary.sections.find(bs => bs.id === s.id);
        s.b = baseSec?.r ?? sectionParent.before; s.d = sectionParent.after - s.b; s.r = sectionParent.after;
        resolvedSectionId = s.id;
      }
    }
    if (applyTotals && rootParent) {
      const baseTotal = baseSummary.totals.nettRate;
      summary.totals = { ...summary.totals, b: baseTotal, d: rootParent.after - baseTotal, nettRate: rootParent.after };
    }

    // File the changed leaf under its immediate Page Group parent, the same
    // key `affectedLeaves` uses for a real simulate response — this is what
    // makes an existing leaf highlight, and what makes a genuinely new one
    // (an "add") appear at all (PageGroupAccordion injects it as a synthetic
    // row when its id isn't already among the group's live leaves). When the
    // backend didn't resolve `parents` at all (observed for some previews —
    // the leaf itself is real, its ripple chain just came back empty), or
    // resolved to a Page Group this page doesn't recognise by id or code
    // (observed for an item added inside an existing activity), file under
    // NO_PARENT_KEY instead of dropping the leaf's diff entirely, or filing
    // it under an unreachable, made-up key: the leaf is real and already
    // rendered somewhere, so `LeafList`'s affectedLeafMap can still pick it
    // up there by id — it just can't drive a Page Group/Section ripple
    // badge, and can't be injected as a synthetic not-yet-existing row
    // (there's nowhere known to inject it).
    const key = resolvedPageGroupId !== undefined ? String(resolvedPageGroupId) : NO_PARENT_KEY;
    const entry: AffectedLeaf = {
      id: leaf.activityId,
      c: leaf.code,
      n: leaf.name,
      q: 1,
      r: leaf.after,
      so: -1,
      b: leaf.before,
      d: leaf.diff,
      // `?? []` matters: a missing/null `breakdown` must still leave `tree`
      // an array (not undefined), so `LeafRow`'s `embeddedTree` check below
      // takes the "render what we have" branch rather than falling through
      // to the per-leaf simulate-breakdown query, which is permanently
      // disabled for this path (no `CensoChange` list to simulate against)
      // and renders nothing at all — see `LeafRow`'s own comment.
      tree: leaf.breakdown ?? [],
      ancestors: leaf.parents.map(par => ({ id: par.activityId, code: par.code })),
    };
    affectedLeaves[key] = [...(affectedLeaves[key] ?? []), entry];
    return { pageGroupId: resolvedPageGroupId, sectionId: resolvedSectionId };
  };

  const primaryLocation = applyLeaf(preview.primary, true);
  if (preview.secondary) applyLeaf(preview.secondary, false);

  // TEMP diagnostics — remove once the Page Group/Section resolution above is
  // confirmed working against the real backend for an item added inside an
  // existing activity's breakdown (as opposed to a brand-new activity).
  // eslint-disable-next-line no-console
  console.debug('[ActivityAssistant][mutationPreview] location resolved for primary leaf', {
    leafCode: preview.primary.code,
    leafActivityId: preview.primary.activityId,
    rawParents: preview.primary.parents,
    resolvedPageGroupId: primaryLocation.pageGroupId,
    resolvedSectionId: primaryLocation.sectionId,
    routedToNoParentKey: primaryLocation.pageGroupId === undefined,
  });

  const result: SimulateResponse = {
    stats: {
      itemsChanged: preview.primary.breakdown.filter(n => !!n.changeKind).length,
      templatesAffected: 0,
      leavesAffected: preview.secondary ? 2 : 1,
      sectionsAffected: 1,
      pageGroupsAffected: 1,
    },
    summary,
    templates: {},
    affectedLeaves,
  };

  return {
    result,
    target: {
      pageGroupId: primaryLocation.pageGroupId,
      sectionId: primaryLocation.sectionId,
      leafId: preview.primary.activityId,
    },
  };
}

// ─── Activity Tree Page ───────────────────────────────────────────────────────

const ActivityTreePage: React.FC = () => {
  const navigate = useNavigate();
  const user = useUser();
  const { state: tenderState } = useTender();
  const queryClient = useQueryClient();
  const [region, setRegion] = useState<string>('');
  const [regionApplied, setRegionApplied] = useState(false);

  useEffect(() => {
    if (regionApplied) return;
    // Project view: prefer the project's own region, fall back to user's region
    const fromProject = tenderState.projectName ? toRegionSlug(tenderState.region) : '';
    const fromUser = toRegionSlug(user?.region ?? '');
    const resolved = fromProject || fromUser;
    if (resolved) {
      setRegion(resolved);
      setRegionApplied(true);
    }
  }, [tenderState.region, tenderState.projectName, user?.region, regionApplied]);

  const isRegional = !tenderState.projectName;
  // "Add New Rates" on a site-specific library is dev-only for now — see the
  // floating bubble's onClick below. Regional is unaffected: it always shows
  // the mode-picker, dev mode or not.
  // Same restriction as /rate-engine/rate-manager (RegionalRate.tsx): editing
  // the Regional Rate Library (no tender in context) affects every future
  // tender in the region, so it's restricted to System Administrator —
  // same role gate as the "Regional Rate Library" button on the Tenders
  // Dashboard (OpportunitiesDashboard.tsx's canViewRegionalRateLibrary).
  // Site-specific rates (a tender is open) stay open to every role that
  // already has access to this page.
  const isSystemAdministrator = user?.roleDisplayName === 'System Administrator';
  // Gates every auto-firing query below, not just the 403 view further down —
  // without this, `enabled: !!region` alone still fires every one of these
  // requests (they're declared before the early-return guard runs, since
  // hooks can't themselves be conditional) even for a non-admin who is about
  // to see nothing but the Access Restricted screen.
  const canAccessRateLibrary = !isRegional || isSystemAdministrator;
  const effectiveRegion = region || 'north-west';
  const siteName = tenderState.projectName || undefined;
  // Verified opportunity number for this site library — undefined for legacy
  // untagged sites, so they keep resolving by siteName alone.
  const { oppNo, isReady: isSiteScopeReady } = useSiteOppNo(effectiveRegion, siteName, tenderState.opportunityNo || undefined);
  const userEmail = user?.email ?? '';
  const expandPersistNamespace = userEmail
    ? `activityTreeExpand_${userEmail}_${siteName ?? effectiveRegion}`
    : null;

  // Hide mutating controls (Reset, Rate Assistant) when viewing a historical
  // version. Deliberately does NOT check tenderState.isViewMode — the
  // Regional Rate Library is a standalone rate-management tool, not a
  // tender workflow page, so the tender's view/edit mode (MainLayout's
  // canEditTender) has no bearing on whether its own controls are usable.
  const isReadOnly = localStorage.getItem('isHistoricalVersion') === 'true';

  // ─── Simulate state ──────────────────────────────────────────────────────

  const [simulateResult, setSimulateResult] = useState<SimulateResponse | null>(null);
  // Identity of the activity a just-confirmed "Add New Activity" created —
  // drives auto-expand-and-highlight in SectionAccordion/PageGroupAccordion/
  // LeafRow (see JustAddedActivityInfo's own doc comment). Cleared after a
  // generous window rather than the moment the highlighted row is first seen:
  // unlike Bill Summaries (where the new row's id is known synchronously),
  // here it also has to survive an async query-invalidation refetch before
  // the row even exists to highlight, so a short bill-summary-style 2s timer
  // would often expire before anything was ever shown.
  const [justAddedActivity, setJustAddedActivity] = useState<JustAddedActivityInfo | null>(null);
  useEffect(() => {
    if (!justAddedActivity) return;
    const timer = setTimeout(() => setJustAddedActivity(null), 8000);
    return () => clearTimeout(timer);
  }, [justAddedActivity]);
  // Section/Page Group/leaf to auto-expand to for a mutation *preview* (dry-run,
  // before confirm) — see `MutationPreviewTarget`'s own doc comment for why this
  // is needed in addition to `justAddedActivity` above. Set alongside
  // `simulateResult` in `handleMutationPreview`, cleared the same way.
  const [previewTarget, setPreviewTarget] = useState<MutationPreviewTarget | null>(null);
  const [simulateChanges, setSimulateChanges] = useState<CensoChange[]>([]);
  const [simulateBatchId, setSimulateBatchId] = useState<string>('');
  const viewMode: ViewMode = simulateResult ? 'diff' : 'current';
  const [isSimulating, setIsSimulating] = useState(false);
  const [showChangedOnly, setShowChangedOnly] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState('');
  const [discardedCodes, setDiscardedCodes] = useState<Set<string>>(new Set());
  const [adjustedRates, setAdjustedRates] = useState<Record<string, number>>({});
  // Incremented on reset-to-regional-defaults so ChatPanel remounts with a fresh session
  const [chatPanelResetKey, setChatPanelResetKey] = useState(0);
  const simulateNonceRef = React.useRef(0);

  // ─── History modal state ─────────────────────────────────────────────────

  const [historyOpen, setHistoryOpen] = useState(false);
  const [sessionHistoryOpen, setSessionHistoryOpen] = useState(false);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [censoMinimized, setCensoMinimized] = useState(true);
  const [applyResultModal, setApplyResultModal] = useState<{ open: boolean; changes: CensoChange[] }>({ open: false, changes: [] });

  // Rate Assistant action-choice prompt — shown every time the assistant is
  // opened from the floating bubble, so the user picks which conversation to
  // start: adjusting rates on the existing library (unchanged flow) or
  // describing a brand new activity to add (separate flow — see censo-panel.tsx).
  const [assistantChoiceOpen, setAssistantChoiceOpen] = useState(false);
  const [assistantMode, setAssistantMode] = useState<CensoAssistantMode>('modify');

  // Shared by "Modify Existing Activity"'s Apply and "Add New Activity"'s
  // confirmed writes — both mutate the same underlying activity tree, so both
  // need the same set of cached queries invalidated for the change to show up
  // immediately instead of on next navigation/refresh.
  const invalidateActivityTreeQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: RATE_ENGINE_QUERY_KEYS.activityInfo(effectiveRegion, siteName, oppNo) });
    queryClient.invalidateQueries({ queryKey: RATE_ENGINE_QUERY_KEYS.activitySummary(effectiveRegion, siteName, oppNo) });
    queryClient.invalidateQueries({ queryKey: RATE_ENGINE_QUERY_KEYS.activityTemplates(effectiveRegion, siteName, oppNo) });
    queryClient.invalidateQueries({ queryKey: [...RATE_ENGINE_QUERY_KEYS.all, 'activityLeaves', effectiveRegion] });
    queryClient.invalidateQueries({ queryKey: [...RATE_ENGINE_QUERY_KEYS.all, 'activityBreakdownNested', effectiveRegion] });
    queryClient.invalidateQueries({ queryKey: [...RATE_ENGINE_QUERY_KEYS.all, 'activityBreakdownSimulated', effectiveRegion] });
    queryClient.invalidateQueries({ queryKey: RATE_ENGINE_QUERY_KEYS.libraryItems(effectiveRegion, siteName, oppNo) });
  }, [queryClient, effectiveRegion, siteName, oppNo]);

  const openAssistant = useCallback((mode: CensoAssistantMode) => {
    // Both assistant modes drive the same `simulateResult`/diff-mode state
    // (see `buildSimulateResultFromMutationPreview`) — switching mode without
    // clearing it would leave a stale diff from the mode just left on screen.
    // Only clears on an actual switch, not a same-mode reopen, so minimising
    // a "Modify Existing Activity" session with unapplied pending changes and
    // reopening it doesn't discard them.
    if (assistantMode !== mode) {
      setSimulateResult(null);
      setSimulateChanges([]);
      setSimulateBatchId('');
      setPreviewTarget(null);
      setShowChangedOnly(false);
    }
    setAssistantMode(mode);
    setAssistantChoiceOpen(false);
    setCensoMinimized(false);
    window.dispatchEvent(
      new CustomEvent('sidebarCollapseRequest', { detail: { collapsed: true } }),
    );
  }, [assistantMode]);

  const refreshMutation = useRefreshSiteLibrary({
    onSuccess: () => {
      // Invalidate tree/summary queries so the UI reflects the reset library
      queryClient.invalidateQueries({ queryKey: RATE_ENGINE_QUERY_KEYS.activitySummary(effectiveRegion, siteName, oppNo) });
      queryClient.invalidateQueries({ queryKey: RATE_ENGINE_QUERY_KEYS.activityInfo(effectiveRegion, siteName, oppNo) });
      queryClient.invalidateQueries({ queryKey: RATE_ENGINE_QUERY_KEYS.activityTemplates(effectiveRegion, siteName, oppNo) });
      queryClient.invalidateQueries({ queryKey: [...RATE_ENGINE_QUERY_KEYS.all, 'activityLeaves', effectiveRegion] });
      queryClient.invalidateQueries({ queryKey: [...RATE_ENGINE_QUERY_KEYS.all, 'activityBreakdownNested', effectiveRegion] });

      // Reset (not just invalidate) the library items cache so the remounted ChatPanel
      // starts with empty items and waits for the fresh fetch before auto-connecting.
      // Using invalidateQueries here would return stale-while-revalidate data, causing
      // the panel to connect with pre-reset rates and then show "connecting" indefinitely
      // when the fresh data arrives and the auto-connect guard blocks a second call.
      queryClient.resetQueries({ queryKey: RATE_ENGINE_QUERY_KEYS.libraryItems(effectiveRegion, siteName, oppNo) });

      // Clear all pending simulation / adjusted-rate state from the previous session
      setSimulateResult(null);
      setSimulateChanges([]);
      setSimulateBatchId('');
      setPreviewTarget(null);
      setShowChangedOnly(false);
      setAdjustedRates({});
      setDiscardedCodes(new Set());

      // Force ChatPanel to remount — the new instance starts with items=[] (cache was
      // reset), waits for the fresh library fetch, then auto-connects with post-reset rates
      setChatPanelResetKey(k => k + 1);

      setResetModalOpen(false);
      Modal.success({ title: 'Rates reset', content: 'Site library has been reset to regional defaults.' });
    },
    onError: (err) => {
      setResetModalOpen(false);
      if (isAxiosError(err) && err.response?.status === 404) {
        Modal.error({ title: 'Site not found', content: 'Site not found under this region.' });
      } else {
        Modal.error({ title: 'Reset failed', content: 'An error occurred. Please try again.' });
      }
    },
  });

  // ─── Queries ─────────────────────────────────────────────────────────────

  const [infoQ, summaryQ] = useQueries({
    queries: [
      {
        queryKey: RATE_ENGINE_QUERY_KEYS.activityInfo(effectiveRegion, siteName, oppNo),
        queryFn: () => rateEngineApi.getActivityInfo(effectiveRegion, siteName, oppNo),
        enabled: !!region && canAccessRateLibrary && isSiteScopeReady,
        staleTime: 60_000,
        retry: 1,
      },
      {
        queryKey: RATE_ENGINE_QUERY_KEYS.activitySummary(effectiveRegion, siteName, oppNo),
        queryFn: () => rateEngineApi.getActivitySummary(effectiveRegion, siteName, oppNo),
        enabled: !!region && canAccessRateLibrary && isSiteScopeReady,
        staleTime: 60_000,
        retry: 1,
      },
    ],
  });

  // The Activity Assistant ("Add New Rates") writes against a single live
  // project per region — there is no separate live project per site, only a
  // separate *rates* library. `infoQ` above is scoped to `siteName` (correct
  // for the rates it renders), so on a site-specific library its `projectId`
  // is not the id the Activity Assistant should mutate. Re-fetch info with no
  // `siteName` so the same `projectId` the Regional Rate Library uses is what
  // gets sent, regardless of which library is currently open. Skipped
  // entirely when `isRegional` (siteName is already undefined, so it would
  // just be a duplicate of `infoQ`).
  const regionalInfoQ = useQuery({
    queryKey: RATE_ENGINE_QUERY_KEYS.activityInfo(effectiveRegion, undefined),
    queryFn: () => rateEngineApi.getActivityInfo(effectiveRegion),
    enabled: !isRegional && !!region && canAccessRateLibrary,
    staleTime: 60_000,
    retry: 1,
  });

  // Templates fetched on library open and cached for the session (used as fallback reference)
  useQuery({
    queryKey: RATE_ENGINE_QUERY_KEYS.activityTemplates(effectiveRegion, siteName, oppNo),
    queryFn: () => rateEngineApi.getActivityTemplates(effectiveRegion, siteName, oppNo),
    enabled: !!region && canAccessRateLibrary && isSiteScopeReady,
    staleTime: 300_000,
    retry: 1,
  });

  // Library items for Censo connection
  const { data: libraryData } = useRegionalLibraryItems(effectiveRegion, siteName, { enabled: !!region && canAccessRateLibrary && isSiteScopeReady }, oppNo);
  const libraryItems = libraryData ?? [];

  const summary: ActivitySummary | undefined = summaryQ.data;

  const sortedSections = useMemo(
    () => summary?.sections.slice().sort((a, b) => {
      const ai = sectionPreferredIndex(a.n);
      const bi = sectionPreferredIndex(b.n);
      if (ai !== bi) return ai - bi;
      return a.so - b.so;
    }) ?? [],
    [summary],
  );

  const displayedSections = useMemo(() => {
    if (!showChangedOnly || !simulateResult) return sortedSections;
    // An unrouted change (see NO_PARENT_KEY) can't be attributed to any one
    // section, so nothing can safely be hidden — show every section rather
    // than risk hiding the one the change actually belongs to.
    if (hasUnroutedChange(simulateResult)) return sortedSections;
    const affectedSectionIds = new Set(
      simulateResult.summary.sections
        .filter(s => s.d !== undefined && s.d !== 0)
        .map(s => s.id),
    );
    // A section also counts as changed when one of its Page Groups has a
    // resolved affected leaf, even if the section/Page Group's own numeric
    // diff nets out to zero (e.g. a newly-added item with a zero rate) —
    // not just `s.d !== 0`.
    const affectedPgIds = affectedPageGroupIds(simulateResult);
    return sortedSections.filter(s =>
      affectedSectionIds.has(s.id) || s.pageGroups.some(pg => affectedPgIds.has(pg.id)),
    );
  }, [sortedSections, showChangedOnly, simulateResult]);

  // ─── Simulate trigger ─────────────────────────────────────────────────────

  const handleChangesReceived = useCallback(async (changes: CensoChange[]) => {
    if (!region || changes.length === 0) return;
    const nonce = ++simulateNonceRef.current;
    setIsSimulating(true);
    try {
      const result = await rateEngineApi.simulateActivitiesNested(
        effectiveRegion,
        { changes },
        siteName,
        oppNo,
      );
      if (nonce !== simulateNonceRef.current) return;
      setSimulateResult(result);
      setSimulateChanges(changes);
      setSimulateBatchId(crypto.randomUUID());
    } catch (err) {
      if (nonce !== simulateNonceRef.current) return;
      if (isAxiosError(err)) {
        if (err.response?.status === 422) {
          const data = err.response.data as { unknownCodes?: string[] };
          const codes = (data.unknownCodes ?? []).join(', ');
          void message.warning(`Some items not found in this library${codes ? `: ${codes}` : ''}`);
        } else if (err.response?.status === 400) {
          void message.error('Invalid simulation request.');
        } else if (err.response?.status === 404) {
          void message.error('Library not found. Please check your region selection.');
        }
      }
    } finally {
      if (nonce === simulateNonceRef.current) {
        setIsSimulating(false);
      }
    }
  }, [region, effectiveRegion, siteName, oppNo]);

  // "Add New Activity" (the Activity Assistant) counterpart to
  // `handleChangesReceived` above — adapts its own preview mechanism
  // (`MutationPreviewResponse`, from `ChatPanel`'s `onMutationPreview`) into
  // the same `simulateResult` state so it drives the identical diff-mode
  // rendering. `'loading'` is a deliberate no-op: it keeps the previous
  // preview on screen while the next one is in flight, same as
  // `isSimulating` doesn't clear `simulateResult` for the "modify" path.
  const handleMutationPreview = useCallback((state: AddNewActivityPreviewState) => {
    if (state.status === 'loading') return;
    if (state.status !== 'ready' || !state.data || !summary) {
      setSimulateResult(null);
      setSimulateChanges([]);
      setSimulateBatchId('');
      setPreviewTarget(null);
      return;
    }
    const adapted = buildSimulateResultFromMutationPreview(state.data, summary);
    setSimulateResult(adapted?.result ?? null);
    setSimulateChanges([]);
    setSimulateBatchId(adapted ? crypto.randomUUID() : '');
    // Force the affected Section/Page Group open so the diff this preview just
    // computed is actually visible — see `MutationPreviewTarget`'s doc comment.
    // `adapted.target` uses this page's own *resolved* ids (matched by id or
    // code inside `buildSimulateResultFromMutationPreview`), not the raw
    // preview response's `parents[].activityId` — those can point at a Page
    // Group/Section id this page doesn't recognise.
    setPreviewTarget(adapted?.target ?? null);
  }, [summary]);

  // ─── Render ───────────────────────────────────────────────────────────────

  if (user?.loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 112px)' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!canAccessRateLibrary) {
    return (
      <div style={{ padding: '24px', minHeight: 'calc(100vh - 112px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Result
          status="403"
          title="Access Restricted"
          subTitle="Only System Administrators can access the Regional Rate Library."
          extra={
            <Button type="primary" icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
              Go Back
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', height: 'calc(100vh - 112px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '16px',
          flexShrink: 0,
          flexWrap: 'wrap',
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          {isRegional ? 'Regional Rate Library' : 'Site Rate Library'}
        </Title>

        <Tooltip
          title={isRegional
            ? 'Viewing the Regional Activity Library.'
            : 'Viewing site-specific activity rates.'}
        >
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '3px 10px', borderRadius: 20,
            backgroundColor: isRegional ? '#fff7ed' : '#f0fdf4',
            border: `1px solid ${isRegional ? '#fdba74' : '#86efac'}`,
            color: isRegional ? '#c2410c' : '#15803d',
            fontSize: 12, fontWeight: 600, letterSpacing: '0.02em',
            cursor: 'default', userSelect: 'none',
          }}>
            <span style={{
              width: 7, height: 7, borderRadius: '50%',
              backgroundColor: isRegional ? '#f97316' : '#22c55e', flexShrink: 0,
            }} />
            {isRegional ? 'Regional Library' : 'Site Rates'}
          </span>
        </Tooltip>

        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          padding: '4px 12px', borderRadius: '20px',
          backgroundColor: '#e6f0ff', border: '1px solid #91b8ff',
          color: '#0B2545', fontSize: '13px', fontWeight: 600, lineHeight: 1.5,
        }}>
          <EnvironmentOutlined style={{ fontSize: '13px', color: '#1890ff' }} />
          {!isRegional
            ? `${tenderState.projectName} | ${REGIONS.find(r => r.value === effectiveRegion)?.label ?? effectiveRegion}`
            : REGIONS.find(r => r.value === effectiveRegion)?.label ?? effectiveRegion}
        </span>

        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          {!isReadOnly && (
            <Tooltip title="View Session History">
              <Button icon={<RobotOutlined />} onClick={() => setSessionHistoryOpen(true)} />
            </Tooltip>
          )}
          <Tooltip title="Switch to Resource View">
            <Button icon={<UnorderedListOutlined />} onClick={() => navigate('/rate-engine/rate-manager')}>
              Resource View
            </Button>
          </Tooltip>
          <Button icon={<HistoryOutlined />} onClick={() => setHistoryOpen(true)}>
            Change History
          </Button>
          {!isRegional && !isReadOnly && (
            <Button
              icon={<ReloadOutlined />}
              danger
              onClick={() => setResetModalOpen(true)}
            >
              Reset to Regional Defaults
            </Button>
          )}
          {isRegional && (
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/')} size="middle">
              Back to Dashboard
            </Button>
          )}
        </div>
      </div>

      <Modal
        open={resetModalOpen}
        title="Reset to Regional Defaults"
        okText="Reset"
        okButtonProps={{ danger: true, loading: refreshMutation.isPending }}
        cancelButtonProps={{ disabled: refreshMutation.isPending }}
        onOk={() => {
          if (!siteName) return;
          refreshMutation.mutate({ region: effectiveRegion, siteName, refreshedBy: userEmail, oppNo });
        }}
        onCancel={() => { if (!refreshMutation.isPending) setResetModalOpen(false); }}
        closable={!refreshMutation.isPending}
      >
        <p>
          Resetting will discard all rate changes made to this site and restore it to the
          current regional library. It will also remove all favourites from the Site Rate
          library and replace them with the favourites from the regional library.
        </p>
        <p>This action cannot be undone. Continue?</p>
      </Modal>

      {/* Site-specific / regional banner */}
      <div style={{
        padding: '10px 16px', flexShrink: 0,
        backgroundColor: isRegional ? '#dbeafe' : '#d1fae5',
        borderLeft: `4px solid ${isRegional ? '#3b82f6' : '#10b981'}`,
      }}>
        <Text strong style={{ fontSize: 13, color: isRegional ? '#1e40af' : '#065f46' }}>
          {isRegional ? 'Regional Rate Library' : 'Site-Specific Rates'}
        </Text>
        <Text style={{ display: 'block', fontSize: 12, color: isRegional ? '#3730a3' : '#047857', marginTop: 1 }}>
          {isRegional
            ? 'Changes here apply to all new tenders in this region — please review carefully before saving.'
            : 'Changes here apply only to this tender and will not affect the regional library or other tenders.'}
        </Text>
      </div>

      {/* Error states */}
      {summaryQ.error && (
        <Alert
          type="error"
          message="Failed to load activity library"
          description="Check your region selection and network connection."
          action={<Button onClick={() => summaryQ.refetch()}>Retry</Button>}
          style={{ margin: 16 }}
        />
      )}

      {/* Body: tree + panel */}
      <div style={{ flex: 1, display: 'flex', gap: 16, minHeight: 0 }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {/* Last updated bar */}
          {infoQ.data?.lastUpdated && (
            <div style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0', flexShrink: 0, background: '#fff' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <HistoryOutlined style={{ marginRight: 5 }} />
                Last updated:{' '}
                <strong>
                  {parseUtc(infoQ.data.lastUpdated).toLocaleString('en-GB', {
                    day: '2-digit', month: '2-digit', year: 'numeric',
                    hour: '2-digit', minute: '2-digit',
                  })}
                </strong>
                {infoQ.data.updatedBy && <>{' '}by <strong>{infoQ.data.updatedBy}</strong></>}
              </Text>
            </div>
          )}

          {/* Loading skeleton */}
          {summaryQ.isLoading && (
            <div style={{ padding: 16 }}>
              {[1, 2, 3, 4, 5].map(i => (
                <Skeleton.Input key={i} active block style={{ marginBottom: 8, height: 44 }} />
              ))}
            </div>
          )}

          {/* Accordion tree */}
          {summary && (
            <div style={{ flex: 1, overflow: 'auto' }}>
              {sortedSections.length === 0 && (
                <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>No sections found for this library.</div>
              )}
              {displayedSections.map(section => (
                <SectionAccordion
                  key={section.id}
                  section={section}
                  region={effectiveRegion}
                  siteName={siteName}
                  oppNo={oppNo}
                  simulateResult={simulateResult ?? undefined}
                  viewMode={viewMode}
                  showChangedOnly={showChangedOnly}
                  simulateChanges={simulateChanges.length > 0 ? simulateChanges : undefined}
                  simulateBatchId={simulateBatchId || undefined}
                  persistNamespace={expandPersistNamespace}
                  justAddedActivity={justAddedActivity}
                  previewTarget={previewTarget}
                />
              ))}
            </div>
          )}
        </div>

        {/* Censo panel — inline, hidden in read-only mode or when minimised. Not mounted
            until the region has settled (regionApplied) so it never auto-connects against
            the transient 'north-west' fallback and then immediately remounts/reconnects. */}
        {regionApplied && (
        <div style={{
          display: isReadOnly || censoMinimized ? 'none' : 'flex',
          width: 360,
          flexShrink: 0,
          minHeight: 0,
          flexDirection: 'column',
          borderRadius: 8,
          border: '1px solid rgba(0,0,0,0.08)',
          overflow: 'hidden',
        }}>
          <ChatPanel
          key={`${effectiveRegion}::${siteName ?? ''}::${assistantMode}::${chatPanelResetKey}`}
          mode={assistantMode}
          projectId={isRegional ? infoQ.data?.projectId : regionalInfoQ.data?.projectId}
          items={libraryItems}
          region={effectiveRegion}
          siteName={siteName}
          oppNo={oppNo}
          tenderName={tenderState.projectName || undefined}
          userEmail={userEmail}
          adjustedRates={adjustedRates}
          onAdjust={(updated) => {
            setAdjustedRates(updated);
            if (Object.keys(updated).length === 0) {
              setSimulateResult(null);
              setSimulateChanges([]);
              setSimulateBatchId('');
              setPreviewTarget(null);
              setShowChangedOnly(false);
            }
          }}
          onApply={(savedChanges) => {
            setSimulateResult(null);
            setSimulateChanges([]);
            setSimulateBatchId('');
            setPreviewTarget(null);
            setAdjustedRates({});
            setShowChangedOnly(false);
            invalidateActivityTreeQueries();
            if (savedChanges.length > 0) {
              setApplyResultModal({ open: true, changes: savedChanges });
            }
          }}
          onSessionIdChange={setCurrentSessionId}
          onDiscardedCodesChange={setDiscardedCodes}
          onChangesReceived={handleChangesReceived}
          onActivityChanged={invalidateActivityTreeQueries}
          onActivityAdded={setJustAddedActivity}
          onMutationPreview={handleMutationPreview}
          isSimulating={isSimulating}
          isOpen={!isReadOnly && !censoMinimized}
          onMinimize={() => setCensoMinimized(true)}
        />
        </div>
        )}
      </div>

      {/* Floating bubble — shown when panel is minimised and not in read-only mode */}
      {regionApplied && censoMinimized && !isReadOnly && (
        <button
          onClick={() => {
            // Regional and site-specific libraries both get the mode-picker
            // (Modify Existing / Add New Activity), for every user.
            setAssistantChoiceOpen(true);
          }}
          title="Open Rate Assistant"
          style={{
            position: 'fixed',
            bottom: 28,
            right: 28,
            width: 58,
            height: 58,
            borderRadius: '50%',
            backgroundColor: '#0B2545',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 16px rgba(11,37,69,0.45)',
            zIndex: 1200,
            transition: 'transform 0.15s ease, box-shadow 0.15s ease',
          }}
          onMouseEnter={e => {
            (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.08)';
            (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 6px 22px rgba(11,37,69,0.55)';
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)';
            (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 16px rgba(11,37,69,0.45)';
          }}
        >
          <RobotOutlined style={{ color: '#fff', fontSize: 24 }} />
          {Object.keys(adjustedRates).length > 0 && (
            <span style={{
              position: 'absolute', top: 4, right: 4,
              width: 17, height: 17, borderRadius: '50%',
              backgroundColor: '#fa8c16', color: '#fff',
              fontSize: 9, fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {Object.keys(adjustedRates).length}
            </span>
          )}
        </button>
      )}

      {/* Rate Assistant action-choice prompt — shown every time the assistant is opened */}
      <Modal
        open={assistantChoiceOpen}
        onCancel={() => setAssistantChoiceOpen(false)}
        footer={null}
        closeIcon={<span style={{ color: 'rgba(255,255,255,0.75)', fontSize: 16 }}>✕</span>}
        width={460}
        styles={{
          content: { padding: 0, borderRadius: 16, overflow: 'hidden' },
          body: { padding: 0 },
        }}
        centered
      >
        <div style={{
          padding: '22px 24px 20px',
          background: 'linear-gradient(135deg, #0B2545 0%, #1c3f72 100%)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: 'rgba(255,255,255,0.12)',
              border: '1px solid rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <RobotOutlined style={{ color: '#fff', fontSize: 18 }} />
            </div>
            <div>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 16, lineHeight: 1.3 }}>
                Rate Assistant
              </div>
              <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12, marginTop: 1 }}>
                What would you like to do?
              </div>
            </div>
          </div>
        </div>

        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10, background: '#f7f8fc' }}>
          {[
            {
              mode: 'modify' as CensoAssistantMode,
              icon: <EditOutlined style={{ fontSize: 18 }} />,
              iconBg: '#e6f4ff',
              iconColor: '#1677ff',
              title: 'Modify Existing Rates',
              desc: 'Ask the AI to adjust rates on the current library by code, name, or group.',
            },
            // Add New Activity (the Activity Assistant) writes to the live
            // project's activity tree. Available against both the Regional
            // Rate Library and a tender's site-specific library — for the
            // latter, `ChatPanel` sends `tenderName` on every write so the
            // change is attributed to this tender, same as "Modify Existing
            // Rates" already does on its own save/apply payload.
            {
              mode: 'addNew' as CensoAssistantMode,
              icon: <PlusOutlined style={{ fontSize: 18 }} />,
              iconBg: '#e9fbe6',
              iconColor: '#389e0d',
              title: 'Add New Rates',
              desc: 'Ask the AI to search this project\'s live activities and make changes to them.',
            },
          ].map((opt) => (
            <button
              key={opt.mode}
              onClick={() => openAssistant(opt.mode)}
              style={{
                display: 'flex', alignItems: 'center', gap: 14,
                width: '100%', textAlign: 'left',
                padding: '14px 16px',
                background: '#fff',
                border: '1.5px solid #e4e8f0',
                borderRadius: 12,
                cursor: 'pointer',
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease, transform 0.1s ease',
              }}
              onMouseEnter={(e) => {
                const el = e.currentTarget as HTMLButtonElement;
                el.style.borderColor = '#0B2545';
                el.style.boxShadow = '0 4px 14px rgba(11,37,69,0.12)';
                el.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget as HTMLButtonElement;
                el.style.borderColor = '#e4e8f0';
                el.style.boxShadow = 'none';
                el.style.transform = 'translateY(0)';
              }}
            >
              <div style={{
                width: 42, height: 42, borderRadius: 10, flexShrink: 0,
                background: opt.iconBg, color: opt.iconColor,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {opt.icon}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: '#1a1a1a' }}>{opt.title}</div>
                <div style={{ fontSize: 12, color: '#8a94a6', marginTop: 2, lineHeight: 1.4 }}>{opt.desc}</div>
              </div>
              <ArrowRightOutlined style={{ color: '#c8cdd8', fontSize: 14, flexShrink: 0 }} />
            </button>
          ))}
        </div>
      </Modal>

      {/* Apply result modal — scrollable table so long change lists are never clipped */}
      <Modal
        open={applyResultModal.open}
        title={
          <span style={{ color: '#389e0d' }}>
            ✓ {applyResultModal.changes.length} rate{applyResultModal.changes.length !== 1 ? 's' : ''} updated
          </span>
        }
        footer={<Button type="primary" onClick={() => setApplyResultModal({ open: false, changes: [] })}>OK</Button>}
        onCancel={() => setApplyResultModal({ open: false, changes: [] })}
        width={620}
      >
        <div style={{ maxHeight: 420, overflowY: 'auto' }}>
          <Table
            size="small"
            pagination={false}
            dataSource={applyResultModal.changes.map((ch, i) => ({ ...ch, key: ch.c + i }))}
            columns={[
              { title: 'Code', dataIndex: 'c', key: 'c', width: 110, render: (v: string) => <span style={{ fontFamily: 'monospace', color: '#1890ff' }}>{v}</span> },
              { title: 'Description', dataIndex: 'n', key: 'n', ellipsis: true },
              { title: 'From', dataIndex: 'or', key: 'or', width: 90, align: 'right' as const, render: (v: number) => <span style={{ fontFamily: 'monospace' }}>£{v.toFixed(2)}</span> },
              { title: 'To', dataIndex: 'nr', key: 'nr', width: 90, align: 'right' as const, render: (v: number) => <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#389e0d' }}>£{v.toFixed(2)}</span> },
            ]}
          />
        </div>
      </Modal>

      {/* Change History Modal */}
      <ChangeHistoryModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        region={effectiveRegion}
        siteName={siteName}
        oppNo={oppNo}
        items={libraryItems}
      />

      {/* AI Session History Modal */}
      <CensoSessionHistoryModal
        open={sessionHistoryOpen}
        onClose={() => setSessionHistoryOpen(false)}
        sessionId={currentSessionId}
        discardedCodes={discardedCodes}
        mode={assistantMode}
      />
    </div>
  );
};

export default ActivityTreePage;
